import React from "react";
import { connect } from "react-redux";
import ReactTooltip from "react-tooltip";

import TabContainer from "../core/components/tabs/TabContainer";
import Tabs from "../core/components/tabs/Tabs";
import Panel from "../core/components/panels/Panel";
import {
    Alignment,
    FlexBehaviour,
    FlexDirection,
    Side,
    Size,
} from "../core/types/system/Layout";
import PanelHeader from "../core/components/panels/PanelHeader";
import Button from "../core/components/buttons/Button";
import Info from "../core/components/info/Info";
import Text from "../core/components/text/Text";
import Error from "../core/components/error/Error";
import Textbox from "../core/components/inputform/Textbox";
import {
    initialiseSettingsDetails,
    KnownSettings,
    setSetting,
    updateKnownSettings,
} from "../core/state/system/Settings";
import RenderOrientations from "../core/types/enums/RenderOrientation";
import { unwrappedDispatch } from "../core/foundation/Dispatching";
import { setPageLoading } from "../core/state/system/UIConfiguration";
import { AppContext } from "../core/types/system/AppContext";
import ButtonThemes from "../core/types/enums/ButtonThemes";
import InputForm from "../core/components/inputform/InputForm";
import InputFormField from "../core/components/inputform/InputFormField";
import InputFieldTypes from "../core/types/enums/ImportFieldTypes";
import PasswordComplexity from "../core/types/system/PasswordComplexity";
import Checkbox from "../core/components/inputform/Checkbox";
import School from "../core/types/core/School";
import GeneralContainer from "../core/components/containers/GeneralContainer";
import {
    cancelSchoolMigration,
    enactSchoolMigration,
    fetchSchoolMigration,
    updateSchool,
} from "../core/state/core/School";
import {
    initialiseEmailServerDetails,
    updateEmailServer,
} from "../core/state/core/EmailServer";
import EmailServer from "../core/types/core/EmailServer";
import Utility from "../core/foundation/Utility";
import SendFromAddress from "../core/types/core/SendFromAddress";
import { addAlert } from "../core/state/system/Alerts";
import { AlertTypes } from "../core/types/system/AlertEntry";
import { initialiseSSOProviderDetails, saveSSO } from "../core/state/core/SSO";
import SSOProvider from "../core/types/core/SSOProvider";
import Configuration from "../core/foundation/Configuration";
import Datastore from "../core/communication/Datastore";
import FederationDetals from "../core/types/core/FederationMetadata";
import DropDown from "../core/components/inputform/DropDown";
import WebRequest from "../core/communication/WebRequest";
import AzImportSettings from "../core/types/core/AzImportSettings";
import AzADTenantInfo from "../core/types/core/AzADTenantInfo";
import AzImportMapping from "../core/types/core/AzImportMapping";
import AzAuthResponse from "../core/types/core/AzAuthResponse";
import OpenIDOrganisation from "../core/types/system/OpenIDOrganisation";
import { Migration, MigrationType } from "../core/types/core/Migration";
import Dialog from "../core/components/dialog/Dialog";
import DialogContent from "../core/components/dialog/DialogContent";
import DialogFooter from "../core/components/dialog/DialogFooter";
import DialogCancel from "../core/components/dialog/DialogCancel";
import DialogOK from "../core/components/dialog/DialogOK";
import RadioList from "../core/components/inputform/RadioList";
import DatePicker from "../core/components/inputform/DatePicker";
import TimePicker from "../core/components/inputform/TimePicker";
import ApplicationState from "../core/foundation/AppState";
import {
    getDashboardUpload,
    initialiseDashboardDetails,
    removeDashboardUpload,
    saveDashboard,
    saveDashboardUpload,
} from "../core/state/core/Dashboard";
import Dashboard, {
    DashboardWidget,
    DashboardWidgetFile,
    DashboardWidgetType,
} from "../core/types/core/Dashboard";
import IconStyles from "../core/types/enums/IconStyles";
import Spacer from "../core/components/containers/Spacer";
import User from "../core/types/core/User";
import { checkUrl, getCheckResultMessage } from "../core/state/system/Urls";
import { IAppState } from "../core/state/Store";

import "./Settings.scss";

interface RouteSettingsProps {
    settings: Record<string, string>;

    setPageLoading: (value: boolean) => void;
    initialiseSettingsDetails: () => Promise<void>;
}

interface RouteSettingsState {
    settings: KnownSettings;
    dirtyFlags: Record<string, boolean>;
}

class RouteSettings extends React.Component<
    RouteSettingsProps,
    RouteSettingsState
> {
    state: RouteSettingsState = {
        settings: null,
        dirtyFlags: {},
    };

    async componentDidMount(): Promise<void> {
        this.props.setPageLoading(true);
        await this.props.initialiseSettingsDetails();
        this.props.setPageLoading(false);

        this.setState({
            settings: new KnownSettings(this.props.settings),
        });
    }

    componentDidUpdate(prevProps: Readonly<RouteSettingsProps>): void {
        if (prevProps.settings !== this.props.settings) {
            this.setState({
                settings: new KnownSettings(this.props.settings),
            });
        }
    }

    dirtyChange(tab: string, dirty: boolean) {
        this.setState(prev => ({
            dirtyFlags: {
                ...prev.dirtyFlags,
                [tab]: dirty,
            },
        }));
    }

    render() {
        return (
            <section>
                <Tabs>
                    <TabContainer
                        header="Security"
                        dirty={this.state.dirtyFlags["Security"]}>
                        <ConnectedSettingsSecurity
                            onDirtyChange={value =>
                                this.dirtyChange("Security", value)
                            }
                        />
                    </TabContainer>
                    <TabContainer
                        header="Email Server Settings"
                        dirty={this.state.dirtyFlags["Email Server Settings"]}>
                        <ConnectedSettingsEmailServer
                            onDirtyChange={value =>
                                this.dirtyChange("Email Server Settings", value)
                            }
                        />
                    </TabContainer>
                    <TabContainer
                        header="SAML SSO"
                        dirty={this.state.dirtyFlags["SAML SSO"]}>
                        <ConnectedSettingsSAML
                            onDirtyChange={value =>
                                this.dirtyChange("SAML SSO", value)
                            }
                        />
                    </TabContainer>
                    <TabContainer
                        header="OpenID SSO"
                        dirty={this.state.dirtyFlags["OpenID SSO"]}>
                        <ConnectedSettingsOpenID
                            onDirtyChange={value =>
                                this.dirtyChange("OpenID SSO", value)
                            }
                        />
                    </TabContainer>
                    <TabContainer
                        header="Storage"
                        dirty={this.state.dirtyFlags["Storage"]}>
                        <ConnectedSettingsStorage
                            onDirtyChange={value =>
                                this.dirtyChange("Storage", value)
                            }
                        />
                    </TabContainer>
                    <TabContainer
                        header="Dashboard"
                        dirty={this.state.dirtyFlags["Dashboard"]}>
                        <ConnectedSettingsDashboard
                            onDirtyChange={value =>
                                this.dirtyChange("Dashboard", value)
                            }
                        />
                    </TabContainer>
                    <TabContainer
                        header="Daily Organiser"
                        dirty={this.state.dirtyFlags["Daily Organiser"]}>
                        <ConnectedSettingsDO
                            onDirtyChange={value =>
                                this.dirtyChange("Daily Organiser", value)
                            }
                        />
                    </TabContainer>
                    <TabContainer
                        header="Portals"
                        dirty={this.state.dirtyFlags["Portals"]}>
                        <ConnectedSettingsPortals
                            onDirtyChange={value =>
                                this.dirtyChange("Portals", value)
                            }
                        />
                    </TabContainer>
                </Tabs>
            </section>
        );
    }
}

const mapStateToProps = (state: IAppState) => {
    const { settings } = state.db;
    return {
        settings: settings.settings,
    };
};

const mapDispatchToProps = dispatch => {
    return {
        setPageLoading: (value: boolean) => dispatch(setPageLoading(value)),
        initialiseSettingsDetails: () =>
            unwrappedDispatch(initialiseSettingsDetails()),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(RouteSettings);

interface SettingsSecurityProps {
    school: School;
    settings: Record<string, string>;

    setPageLoading: (value: boolean) => void;
    onDirtyChange?: (dirty: boolean) => void;
    updateSchool: (school: School) => void;
}

interface SettingsSecurityState {
    school: School;
    settings: KnownSettings;
    password: PasswordComplexity;
    dirty: boolean;
}

class SettingsSecurity extends React.Component<
    SettingsSecurityProps,
    SettingsSecurityState
> {
    static contextType = AppContext;
    context!: React.ContextType<typeof AppContext>;

    state: SettingsSecurityState = {
        school: null,
        settings: null,
        password: null,
        dirty: false,
    };

    async componentDidMount(): Promise<void> {
        if (!!this.props.settings)
            this.setState(
                {
                    school: this.props.school,
                    settings: new KnownSettings(this.props.settings),
                },
                this.parseComplexity.bind(this),
            );
    }

    componentDidUpdate(prevProps: Readonly<SettingsSecurityProps>): void {
        if (prevProps.settings !== this.props.settings)
            this.setState(
                {
                    school: this.props.school,
                    settings: new KnownSettings(this.props.settings),
                },
                this.parseComplexity.bind(this),
            );
    }

    parseComplexity() {
        this.setState({
            password: new PasswordComplexity(
                this.state.settings?.passwordComplexity,
            ),
        });
    }

    update(name: string, value: any) {
        this.props.onDirtyChange?.(true);

        const temp = new KnownSettings(this.state.settings.data);
        temp[name] = value;
        this.setState({
            settings: temp,
            dirty: true,
        });
    }

    updatePassword(value: PasswordComplexity) {
        this.props.onDirtyChange?.(true);

        this.setState({
            password: value,
            dirty: true,
        });
    }

    updateSchool(name: string, value: any) {
        this.props.onDirtyChange?.(true);

        this.setState(prev => ({
            school: {
                ...prev.school,
                [name]: value,
            },
            dirty: true,
        }));
    }

    async reset() {
        this.props.onDirtyChange?.(false);

        this.setState(
            {
                school: this.props.school,
                settings: new KnownSettings(this.props.settings),
                dirty: false,
            },
            this.parseComplexity.bind(this),
        );
    }

    async save() {
        if (await this.context.verifyCredentials()) {
            try {
                this.props.setPageLoading(true);

                const temp = new KnownSettings(this.state.settings.data);
                temp.passwordComplexity = this.state.password.toMask();
                await Promise.all([
                    updateKnownSettings(temp),
                    this.props.updateSchool(this.state.school),
                ]);

                this.props.onDirtyChange?.(false);

                this.setState({
                    dirty: false,
                });
            } finally {
                this.props.setPageLoading(false);
            }
        }
    }

    render() {
        return (
            <Panel>
                <PanelHeader direction={RenderOrientations.RightLeft}>
                    <Button
                        icon="save"
                        secure={true}
                        theme={ButtonThemes.Theme}
                        disabled={!this.state.dirty}
                        onClick={this.save.bind(this)}
                        tooltip="Save changes"
                        tooltipPlace="bottom"
                    />
                    <Button
                        icon="history"
                        clearanceSide={Side.Right}
                        clearanceSize={Size.Small}
                        disabled={!this.state.dirty}
                        onClick={this.reset.bind(this)}
                        tooltip="Reset to last saved"
                        tooltipPlace="bottom"
                    />
                </PanelHeader>

                <Panel>
                    <PanelHeader>
                        <h1>Application Settings</h1>
                    </PanelHeader>

                    <Text>
                        These settings allow you to manage how long a user can
                        be idle in an application before they are required to
                        re-authenticate.
                    </Text>

                    <Panel
                        clearanceSide={Side.Top}
                        clearanceSize={Size.Small}>
                        <PanelHeader>
                            <h2>
                                Authentication session timeout period (in
                                minutes)
                            </h2>
                        </PanelHeader>

                        <Info>
                            How many minutes should a user remain logged in?
                            (This must be a value greater than 4)
                        </Info>

                        <Textbox
                            type="number"
                            min={5}
                            max={99999}
                            width="200px"
                            value={this.state.settings?.sessionTimeOut ?? 30}
                            onChange={value =>
                                this.update("sessionTimeOut", value)
                            }
                        />
                    </Panel>
                </Panel>

                <Panel clearanceSide={Side.Top}>
                    <PanelHeader>
                        <h1>Password Complexity</h1>
                    </PanelHeader>

                    <Text>
                        These settings allow you to manage how complex user
                        passwords must be including their length and character
                        type inclusion.
                    </Text>

                    <Panel
                        clearanceSide={Side.Top}
                        clearanceSize={Size.Small}>
                        <InputForm
                            binding={this.state.password}
                            display={RenderOrientations.Vertical}
                            fieldDisplay={RenderOrientations.Horizontal}
                            onChange={this.updatePassword.bind(this)}>
                            <InputFormField
                                fieldWidth={200}
                                labelBold={true}
                                minNumber={14}
                                field="passwordMinLength"
                                type={InputFieldTypes.Number}
                                tooltip="Passwords must be 14 or more characters"
                                label="Password minimum length"
                            />
                            <InputFormField
                                labelBold={true}
                                field="passwordIncludeUpperLetter"
                                type={InputFieldTypes.Check}
                                tooltip="Should users have to include at least one letter?"
                                label="Passwords must include upper case letter"
                            />
                            <InputFormField
                                labelBold={true}
                                field="passwordIncludeLowerLetter"
                                type={InputFieldTypes.Check}
                                tooltip="Should users have to include at least one letter?"
                                label="Passwords must include lower case letter"
                            />
                            <InputFormField
                                labelBold={true}
                                field="passwordIncludeNumber"
                                type={InputFieldTypes.Check}
                                tooltip="Should users have to include at least one number?"
                                label="Passwords must include number"
                            />
                            <InputFormField
                                labelBold={true}
                                field="passwordIncludeSpecial"
                                type={InputFieldTypes.Check}
                                tooltip="Should users have to include at least one special character?"
                                label="Passwords must include special character"
                            />
                            <InputFormField
                                labelBold={true}
                                field="passwordExcludeNames"
                                type={InputFieldTypes.Check}
                                tooltip="Should users be restricted from using their username, first name and last name in their password?"
                                label="Passwords can't include the users names"
                            />
                        </InputForm>
                    </Panel>
                </Panel>

                <Panel clearanceSide={Side.Top}>
                    <PanelHeader>
                        <h1>Password Expiry Settings</h1>
                    </PanelHeader>

                    <Panel
                        clearanceSide={Side.Top}
                        clearanceSize={Size.Small}>
                        <Checkbox
                            label="Enable password expiry"
                            value={
                                this.state.settings?.enablePasswordExpiry ??
                                false
                            }
                            onChange={value =>
                                this.update("enablePasswordExpiry", value)
                            }
                        />

                        {!!this.state.settings?.enablePasswordExpiry && (
                            <InputForm
                                clearanceSide={Side.Top}
                                binding={this.state.settings}
                                display={RenderOrientations.Vertical}
                                fieldDisplay={RenderOrientations.Horizontal}
                                onChange={this.updatePassword.bind(this)}>
                                <InputFormField
                                    fieldWidth={200}
                                    labelBold={true}
                                    minNumber={0}
                                    defaultValue={0}
                                    field="passwordExpiredAfterNMonth"
                                    type={InputFieldTypes.Number}
                                    label="Password expire after (in months)"
                                />
                                <InputFormField
                                    fieldWidth={200}
                                    labelBold={true}
                                    minNumber={0}
                                    maxNumber={5}
                                    defaultValue={0}
                                    field="previousNBlockedPasswords"
                                    type={InputFieldTypes.Number}
                                    label="How many previous passwords cannot be used"
                                />
                            </InputForm>
                        )}
                    </Panel>
                </Panel>

                <Panel clearanceSide={Side.Top}>
                    <PanelHeader>
                        <h1>Two-Factor Settings</h1>
                    </PanelHeader>

                    <Panel
                        clearanceSide={Side.Top}
                        clearanceSize={Size.Small}>
                        <Checkbox
                            label="Require two-factor authentication for all users"
                            value={this.state.school?.requireTwoFactor ?? false}
                            onChange={value =>
                                this.updateSchool("requireTwoFactor", value)
                            }
                        />

                        <GeneralContainer
                            clearanceSide={Side.Left}
                            clearanceSize={Size.Large}>
                            <Info clearanceSide={Side.Bottom}>
                                Users who do not have two-factor authentication
                                enabled will be prompted to enable it the next
                                time they sign-in.
                            </Info>

                            {this.state.school?.requireTwoFactor && (
                                <>
                                    <Checkbox
                                        label="Require users who sign-in using a passkey to also provide their second factor"
                                        value={
                                            this.state.school
                                                ?.requireTwoFactorFido2 ?? false
                                        }
                                        onChange={value =>
                                            this.updateSchool(
                                                "requireTwoFactorFido2",
                                                value,
                                            )
                                        }
                                    />

                                    <GeneralContainer
                                        clearanceSide={Side.Left}
                                        clearanceSize={Size.Large}>
                                        <Info clearanceSide={Side.Bottom}>
                                            Passkeys use secure certificates for
                                            authentication, providing enhanced
                                            security compared to usernames and
                                            passwords. They usually don't
                                            require two-factor authentication.
                                            If you're worried about device
                                            security, enabling passkeys can
                                            reduce the risk of account
                                            compromise if a device is
                                            compromised.
                                        </Info>
                                    </GeneralContainer>
                                </>
                            )}
                        </GeneralContainer>
                    </Panel>
                </Panel>
            </Panel>
        );
    }
}

const mapSecurityStateToProps = (state: IAppState) => {
    const { school, settings } = state.db;
    return {
        school: school.school,
        settings: settings.settings,
    };
};

const mapSecurityDispatchToProps = dispatch => {
    return {
        setPageLoading: (value: boolean) => dispatch(setPageLoading(value)),
        updateSchool: (school: School) =>
            unwrappedDispatch(updateSchool(school)),
    };
};

const ConnectedSettingsSecurity = connect(
    mapSecurityStateToProps,
    mapSecurityDispatchToProps,
)(SettingsSecurity);

interface SettingsEmailServerProps {
    emailServer: EmailServer;

    setPageLoading: (value: boolean) => void;
    onDirtyChange?: (dirty: boolean) => void;
    initialiseEmailServerDetails: () => Promise<void>;
    updateEmailServer: (emailServer: EmailServer) => void;
}

interface SettingsEmailServerState {
    emailServer: EmailServer;
    dirty: boolean;
}

class SettingsEmailServer extends React.Component<
    SettingsEmailServerProps,
    SettingsEmailServerState
> {
    static contextType = AppContext;
    context!: React.ContextType<typeof AppContext>;

    state: SettingsEmailServerState = {
        emailServer: null,
        dirty: false,
    };

    async componentDidMount(): Promise<void> {
        this.props.setPageLoading(true);
        await this.props.initialiseEmailServerDetails();
        this.props.setPageLoading(false);

        if (!!this.props.emailServer)
            this.setState({
                emailServer: new EmailServer(this.props.emailServer),
            });
    }

    componentDidUpdate(prevProps: Readonly<SettingsEmailServerProps>): void {
        if (prevProps.emailServer !== this.props.emailServer)
            this.setState({
                emailServer: new EmailServer(this.props.emailServer),
            });
    }

    serverStatusClass() {
        return this.state.emailServer?.serverTesting
            ? "spin"
            : Utility.notNull(this.state.emailServer?.tested)
            ? this.state.emailServer.tested
                ? "green"
                : "red"
            : "";
    }

    serverStatusText() {
        return this.state.emailServer?.serverTesting
            ? "autorenew"
            : Utility.notNull(this.state.emailServer?.tested)
            ? this.state.emailServer.tested
                ? "done"
                : "error_outline"
            : "";
    }

    sendFromStatusClass(item: SendFromAddress) {
        return this.state.emailServer.serverTesting
            ? "spin"
            : Utility.notNull(item.tested)
            ? item.tested
                ? "green"
                : "red"
            : "";
    }

    sendFromStatusText(item: SendFromAddress) {
        return this.state.emailServer.serverTesting
            ? "autorenew"
            : Utility.notNull(item.tested)
            ? item.tested
                ? "done"
                : "error_outline"
            : "";
    }

    async removeSendFrom(address: string) {
        const result = await addAlert({
            type: AlertTypes.Confirm,
            icon: "warning",
            styleColor: "yellow",
            title: "Confirm removal",
            text: `Are you sure you want to remove the send-from address ${address}?`,
        });

        if (!result.response) return;

        const temp = new EmailServer(this.state.emailServer);
        temp.sendFromAddresses = temp.sendFromAddresses.filter(
            x => x.address !== address,
        );

        this.props.onDirtyChange?.(true);

        this.setState({
            emailServer: temp,
            dirty: true,
        });
    }

    update(name: string, value: any) {
        this.props.onDirtyChange?.(true);

        const temp = new EmailServer(this.state.emailServer);
        temp[name] = value;
        this.setState({
            emailServer: temp,
            dirty: true,
        });
    }

    updateSendfrom(address: string, name: string, value: any) {
        this.props.onDirtyChange?.(true);

        const temp = new EmailServer(this.state.emailServer);
        const send = temp.sendFromAddresses.find(x => x.address === address);

        if (send) send[name] = value;

        this.setState({
            emailServer: temp,
            dirty: true,
        });
    }

    async test() {
        this.reset();

        await this.save();
    }

    reset() {
        this.props.onDirtyChange?.(false);

        this.setState({
            emailServer: new EmailServer(this.props.emailServer),
            dirty: false,
        });
    }

    async save() {
        if (await this.context.verifyCredentials()) {
            try {
                this.props.setPageLoading(true);

                await Promise.all([
                    this.props.updateEmailServer(this.state.emailServer),
                ]);

                this.props.onDirtyChange?.(false);

                this.setState({
                    dirty: false,
                });
            } finally {
                this.props.setPageLoading(false);
            }
        }
    }

    render() {
        return (
            <Panel>
                <PanelHeader direction={RenderOrientations.RightLeft}>
                    <Button
                        icon="save"
                        secure={true}
                        theme={ButtonThemes.Theme}
                        disabled={!this.state.dirty}
                        onClick={this.save.bind(this)}
                        tooltip="Save changes"
                        tooltipPlace="bottom"
                    />
                    <Button
                        icon="cached"
                        clearanceSide={Side.Right}
                        clearanceSize={Size.Small}
                        onClick={this.test.bind(this)}
                        tooltip="Test this email server configuration"
                        tooltipPlace="bottom"
                    />
                    <Button
                        icon="history"
                        clearanceSide={Side.Right}
                        clearanceSize={Size.Small}
                        disabled={!this.state.dirty}
                        onClick={this.reset.bind(this)}
                        tooltip="Reset to last saved"
                        tooltipPlace="bottom"
                    />
                </PanelHeader>

                <Panel>
                    <PanelHeader>
                        <h1>Server Details</h1>

                        <GeneralContainer
                            flex={FlexDirection.Row}
                            alignment={Alignment.Right}>
                            {this.state.emailServer?.errorMessage && (
                                <Error
                                    bufferSide={Side.Right}
                                    inline={true}>
                                    {this.state.emailServer.errorMessage}
                                </Error>
                            )}
                            <i className={this.serverStatusClass()}>
                                {this.serverStatusText()}
                            </i>
                        </GeneralContainer>
                    </PanelHeader>

                    <Text>
                        The details of your email server. We automatically check
                        if we can connect to your server, however we recommend
                        that you send a test email to ensure the entries are
                        correct.
                    </Text>

                    <Panel
                        clearanceSide={Side.Top}
                        clearanceSize={Size.Small}>
                        <PanelHeader>
                            <h2>SMTP Server Address</h2>
                        </PanelHeader>

                        <Info>
                            The address at which your email server can be
                            contacted, this may either be a URL or an IP
                            address.
                        </Info>

                        <Textbox
                            value={this.state.emailServer?.address}
                            onChange={value => this.update("address", value)}
                        />
                    </Panel>

                    <Panel
                        clearanceSide={Side.Top}
                        clearanceSize={Size.Small}>
                        <PanelHeader>
                            <h2>SMTP Port</h2>
                        </PanelHeader>

                        <Info>
                            The port the SMTP server is running on, the default
                            port is normally 25, 465 or 587 for SSL/TLS.
                        </Info>

                        <Textbox
                            type="number"
                            min={1}
                            max={65535}
                            width="200px"
                            value={String(this.state.emailServer?.port)}
                            onChange={value =>
                                this.update("port", Number(value))
                            }
                        />
                    </Panel>
                </Panel>

                <Panel clearanceSide={Side.Top}>
                    <PanelHeader>
                        <h1>Security</h1>
                    </PanelHeader>

                    <Panel
                        clearanceSide={Side.Top}
                        clearanceSize={Size.Small}>
                        <PanelHeader>
                            <h2>Security Options</h2>
                        </PanelHeader>

                        <GeneralContainer flex={FlexDirection.Row}>
                            <Checkbox
                                label="Use Secure Connection"
                                tooltip="Should the Timetabling Solutions Portal use a TLS connection to connect to your server."
                                value={this.state.emailServer?.useSSL ?? false}
                                onChange={value => this.update("useSSL", value)}
                            />
                            <Checkbox
                                label="Use Credentials"
                                tooltip="When connecting to your email server a username and password may be required, if they are check this box and complete the details below."
                                value={
                                    this.state.emailServer?.useCredentials ??
                                    false
                                }
                                onChange={value =>
                                    this.update("useCredentials", value)
                                }
                            />
                        </GeneralContainer>
                    </Panel>

                    <Panel
                        clearanceSide={Side.Top}
                        clearanceSize={Size.Small}>
                        <PanelHeader>
                            <h2>Username</h2>
                        </PanelHeader>

                        <Info>
                            The username to use when connecting to the email
                            server.
                        </Info>

                        <Textbox
                            width="300px"
                            value={this.state.emailServer?.userName}
                            disabled={!this.state.emailServer?.useCredentials}
                            onChange={value => this.update("userName", value)}
                        />
                    </Panel>

                    <Panel
                        clearanceSide={Side.Top}
                        clearanceSize={Size.Small}>
                        <PanelHeader>
                            <h2>Password</h2>
                        </PanelHeader>

                        <Info>
                            The password to use when connecting to the email
                            server.
                        </Info>

                        <Textbox
                            width="300px"
                            value={this.state.emailServer?.password}
                            disabled={!this.state.emailServer?.useCredentials}
                            onChange={value => this.update("password", value)}
                        />
                    </Panel>
                </Panel>

                <Panel clearanceSide={Side.Top}>
                    <PanelHeader>
                        <h1>Email Send-From Addresses</h1>
                    </PanelHeader>

                    {!this.state.emailServer?.sendFromAddresses?.length ? (
                        <Text>No send-from addresses have been entered.</Text>
                    ) : (
                        this.state.emailServer.sendFromAddresses.map(
                            (item, index) => (
                                <Panel
                                    key={index}
                                    className="list-item"
                                    clearanceSide={Side.Top}
                                    clearanceSize={Size.Small}
                                    flex={FlexDirection.Row}>
                                    <i className="col-pane">email</i>

                                    <Text
                                        bufferSide={Side.Horizontal}
                                        inline={true}
                                        flexBehaviour={FlexBehaviour.Center}>
                                        {item.address}
                                    </Text>

                                    <Checkbox
                                        label="Default"
                                        className="col-pane"
                                        value={item.default ?? false}
                                        onChange={value =>
                                            this.updateSendfrom(
                                                item.address,
                                                "default",
                                                value,
                                            )
                                        }
                                    />

                                    {item.errorMessage && (
                                        <Error
                                            bufferSide={Side.Horizontal}
                                            inline={true}
                                            flexBehaviour={FlexBehaviour.Min}>
                                            {item.errorMessage}
                                        </Error>
                                    )}

                                    <i
                                        className={`col-pane ${this.sendFromStatusClass(
                                            item,
                                        )}`}>
                                        {this.sendFromStatusText(item)}
                                    </i>

                                    <Button
                                        icon="close"
                                        className="col-pane remove"
                                        onClick={this.removeSendFrom.bind(
                                            this,
                                            item.address,
                                        )}
                                        tooltip="Remove this send-from address"
                                        tooltipPlace="bottom"
                                    />
                                </Panel>
                            ),
                        )
                    )}
                </Panel>
            </Panel>
        );
    }
}

const mapEmailServerStateToProps = (state: IAppState) => {
    const { emailServers } = state.db;
    return {
        emailServer: emailServers.emailServer,
    };
};

const mapEmailServerDispatchToProps = dispatch => {
    return {
        setPageLoading: (value: boolean) => dispatch(setPageLoading(value)),
        initialiseEmailServerDetails: () =>
            unwrappedDispatch(initialiseEmailServerDetails()),
        updateEmailServer: (emailServer: EmailServer) =>
            unwrappedDispatch(updateEmailServer(emailServer)),
    };
};

const ConnectedSettingsEmailServer = connect(
    mapEmailServerStateToProps,
    mapEmailServerDispatchToProps,
)(SettingsEmailServer);

interface SettingsSAMLProps {
    school: School;
    provider: SSOProvider;
    settings: Record<string, string>;

    setPageLoading: (value: boolean) => void;
    onDirtyChange?: (dirty: boolean) => void;
    initialiseSSOProviderDetails: () => Promise<void>;
    saveSSO: (sso: SSOProvider) => void;
    updateSchool: (school: School) => void;
}

interface SettingsSAMLState {
    school: School;
    provider: SSOProvider;
    dirty: boolean;
}

class SettingsSAML extends React.Component<
    SettingsSAMLProps,
    SettingsSAMLState
> {
    static contextType = AppContext;
    context!: React.ContextType<typeof AppContext>;

    state: SettingsSAMLState = {
        school: null,
        provider: null,
        dirty: false,
    };

    async componentDidMount(): Promise<void> {
        this.props.setPageLoading(true);
        await this.props.initialiseSSOProviderDetails();
        this.props.setPageLoading(false);

        if (!!this.props.provider)
            this.setState({
                school: this.props.school,
                provider: new SSOProvider(this.props.provider),
            });
    }

    componentDidUpdate(prevProps: Readonly<SettingsSAMLProps>): void {
        if (
            prevProps.school !== this.props.school ||
            prevProps.provider !== this.props.provider
        )
            this.setState({
                school: this.props.school,
                provider: new SSOProvider(this.props.provider),
            });
    }

    update(name: string, value: any) {
        this.props.onDirtyChange?.(true);

        const temp = new SSOProvider(this.state.provider);
        temp[name] = value;
        this.setState({
            provider: temp,
            dirty: true,
        });
    }

    updateSchool(name: string, value: any) {
        this.props.onDirtyChange?.(true);

        const temp = { ...this.state.school };
        temp[name] = value;
        this.setState({
            school: temp,
            dirty: true,
        });
    }

    async configure() {
        const current = this.props.settings["SSO-Config"];

        const result = await addAlert({
            type: AlertTypes.Prompt,
            title: "Configure Using Federation Metadata",
            prompts: [
                {
                    type: InputFieldTypes.Text,
                    display: "Federation Metadata URL",
                    value: current ?? "",
                },
            ],
        });

        if (!result.response) return;

        const url = result.promptResults[0].value;
        setSetting("SSO-Config", url);

        const details = await Datastore.getType<FederationDetals>(
            "api/SSO/SAML/FederationDetails?endpoint=" +
                encodeURIComponent(url),
        );

        this.props.onDirtyChange?.(true);

        this.setState({
            provider: new SSOProvider({
                login: details.metadata.idPSsoDescriptor.singleSignOnServices[0]
                    .location,
                logout: details.metadata.idPSsoDescriptor
                    .singleLogoutServices[0].location,
                fingerprint: details.certificate.thumbprint,
            }),
            dirty: true,
        });
    }

    metadata() {}

    reset() {
        this.props.onDirtyChange?.(false);

        this.setState({
            school: this.props.school,
            provider: new SSOProvider(this.props.provider),
            dirty: false,
        });
    }

    async save() {
        if (await this.context.verifyCredentials()) {
            try {
                this.props.setPageLoading(true);

                await Promise.all([
                    this.props.updateSchool(this.state.school),
                    this.props.saveSSO(this.state.provider),
                ]);

                this.props.onDirtyChange?.(false);

                this.setState({
                    dirty: false,
                });
            } finally {
                this.props.setPageLoading(false);
            }
        }
    }

    render() {
        return (
            <Panel>
                <PanelHeader direction={RenderOrientations.RightLeft}>
                    <Button
                        icon="save"
                        secure={true}
                        theme={ButtonThemes.Theme}
                        disabled={!this.state.dirty}
                        onClick={this.save.bind(this)}
                        tooltip="Save changes"
                        tooltipPlace="bottom"
                    />
                    <Button
                        icon="history"
                        clearanceSide={Side.Right}
                        clearanceSize={Size.Small}
                        disabled={!this.state.dirty}
                        onClick={this.reset.bind(this)}
                        tooltip="Reset to last saved"
                        tooltipPlace="bottom"
                    />
                </PanelHeader>

                <Panel>
                    <Error>
                        Note: Timetabling Solutions V9 desktop application does
                        not support authentication using SSO, V10 does.
                    </Error>
                </Panel>

                <Panel clearanceSide={Side.Top}>
                    <PanelHeader>
                        <h1>Use SAML</h1>
                    </PanelHeader>

                    <Checkbox
                        label="Enable SAML SSO"
                        value={this.state.school?.authenticationMode === "SAML"}
                        onChange={value =>
                            this.updateSchool(
                                "authenticationMode",
                                value ? "SAML" : "TDS",
                            )
                        }
                    />
                </Panel>

                <Panel clearanceSide={Side.Top}>
                    <PanelHeader>
                        <h1>Configuration</h1>
                    </PanelHeader>

                    <Panel
                        flex={FlexDirection.Row}
                        flexSpaced={true}>
                        <Button
                            className="col"
                            disabled={
                                this.state.school?.authenticationMode !== "SAML"
                            }
                            text="Configuration Using Federation Metadata"
                            tooltip="Click here if your SAML IdP has a Federation Metadata endpoint to use it to retrieve configuration"
                            tooltipPlace="bottom"
                            onClick={this.configure.bind(this)}
                        />
                        <Button
                            className="col"
                            disabled={
                                this.state.school?.authenticationMode !== "SAML"
                            }
                            text="Timetable Datastore SAML Metadata"
                            tooltip="Click here to download your Timetable Datastore SAML metadata configuration for use in setting up your IdP"
                            tooltipPlace="bottom"
                            onClick={this.metadata.bind(this)}
                        />
                    </Panel>

                    <Panel clearanceSide={Side.Top}>
                        <PanelHeader>
                            <h2>Login URL</h2>
                        </PanelHeader>

                        <Info>
                            The Timetabling Solutions apps will redirect users
                            to this URL to login. You can get this from your
                            SAML Identity Provider.
                        </Info>

                        <Textbox
                            value={this.state.provider?.login}
                            onChange={value => this.update("login", value)}
                            disabled={
                                this.state.school?.authenticationMode !== "SAML"
                            }
                        />
                    </Panel>

                    <Panel clearanceSide={Side.Top}>
                        <PanelHeader>
                            <h2>Logout URL</h2>
                        </PanelHeader>

                        <Info>
                            Optional logout URL to which users will be sent to
                            when they logout of the Timetable Solutions apps.
                        </Info>

                        <Textbox
                            value={this.state.provider?.logout}
                            onChange={value => this.update("logout", value)}
                            disabled={
                                this.state.school?.authenticationMode !== "SAML"
                            }
                        />
                    </Panel>

                    <Panel clearanceSide={Side.Top}>
                        <PanelHeader>
                            <h2>Certificate Fingerprint</h2>
                        </PanelHeader>

                        <Info>
                            Fingerprint (SHA-256) of the certificate used for
                            signing SAML assertions provided by your SAML
                            Identity Provider.
                            <br />
                            This will be used to validate authentication
                            information. (Please ensure any colons are removed
                            from the fingerprint value)
                        </Info>

                        <Textbox
                            value={this.state.provider?.fingerprint}
                            onChange={value =>
                                this.update("fingerprint", value)
                            }
                            disabled={
                                this.state.school?.authenticationMode !== "SAML"
                            }
                        />
                    </Panel>
                </Panel>

                <Panel clearanceSide={Side.Top}>
                    <PanelHeader>
                        <h1>Custom Domain</h1>
                    </PanelHeader>

                    <Panel clearanceSide={Side.Top}>
                        <PanelHeader>
                            <h2>Domain Name</h2>
                        </PanelHeader>

                        <Info>
                            A unique subdomain for your school, that when used
                            will automatically re-direct users to your SSO
                            platform for authentication
                        </Info>

                        <Textbox
                            value={this.state.school?.authenticationDomain}
                            onChange={value =>
                                this.updateSchool("authenticationDomain", value)
                            }
                            disabled={
                                this.state.school?.authenticationMode !== "SAML"
                            }
                        />

                        {this.state.school?.authenticationDomain && (
                            <Text clearanceSide={Side.Top}>
                                With this set up, users can go to{" "}
                                <b>
                                    {this.state.school.authenticationDomain}.
                                    {Configuration.customDomainsSuffix}
                                </b>{" "}
                                to be automatically redirected to your SSO login
                                page.
                            </Text>
                        )}
                    </Panel>
                </Panel>
            </Panel>
        );
    }
}

const mapSAMLStateToProps = (state: IAppState) => {
    const { sso, school, settings } = state.db;
    return {
        school: school.school,
        provider: sso.provider,
        settings: settings.settings,
    };
};

const mapSAMLDispatchToProps = dispatch => {
    return {
        setPageLoading: (value: boolean) => dispatch(setPageLoading(value)),
        initialiseSSOProviderDetails: () =>
            unwrappedDispatch(initialiseSSOProviderDetails()),
        saveSSO: (sso: SSOProvider) => unwrappedDispatch(saveSSO(sso)),
        updateSchool: (school: School) =>
            unwrappedDispatch(updateSchool(school)),
    };
};

const ConnectedSettingsSAML = connect(
    mapSAMLStateToProps,
    mapSAMLDispatchToProps,
)(SettingsSAML);

interface SettingsOpenIDProps {
    school: School;
    settings: Record<string, string>;

    setPageLoading: (value: boolean) => void;
    onDirtyChange?: (dirty: boolean) => void;
    initialiseSSOProviderDetails: () => Promise<void>;
    saveSSO: (sso: SSOProvider) => void;
    updateSchool: (school: School) => void;
}

interface SettingsOpenIDState {
    school: School;
    settings: KnownSettings;
    dirty: boolean;
    azSettings?: AzImportSettings;
    azMappings?: AzImportMapping;
}

class SettingsOpenID extends React.Component<
    SettingsOpenIDProps,
    SettingsOpenIDState
> {
    static contextType = AppContext;
    context!: React.ContextType<typeof AppContext>;

    state: SettingsOpenIDState = {
        school: null,
        settings: null,
        dirty: false,
    };

    async componentDidMount(): Promise<void> {
        this.props.setPageLoading(true);
        await this.props.initialiseSSOProviderDetails();
        this.props.setPageLoading(false);

        if (!!this.props.settings)
            this.setState({
                school: this.props.school,
                settings: new KnownSettings(this.props.settings),
            });
    }

    componentDidUpdate(prevProps: Readonly<SettingsOpenIDProps>): void {
        if (
            prevProps.school !== this.props.school ||
            prevProps.settings !== this.props.settings
        )
            this.setState({
                school: this.props.school,
                settings: new KnownSettings(this.props.settings),
            });
    }

    update(name: string, value: any) {
        this.props.onDirtyChange?.(true);

        return new Promise<void>(resolve => {
            const temp = new KnownSettings(this.state.settings.data);
            temp[name] = value;
            this.setState(
                {
                    settings: temp,
                    dirty: true,
                },
                () => resolve(),
            );
        });
    }

    updateSchool(name: string, value: any) {
        this.props.onDirtyChange?.(true);

        const temp = { ...this.state.school };
        temp[name] = value;
        this.setState({
            school: temp,
            dirty: true,
        });

        if (name === "authenticationMode" && value === "OpenID")
            this.loadAzureSettings();
    }

    async loadAzureSettings() {
        const settings = await Datastore.getType<AzImportSettings>(
            "api/Azure/ADApp/Settings",
        );

        this.setState({
            azSettings: settings,
        });
    }

    async azureLogin() {
        const location = {
            top:
                window.screenY + (window.outerHeight - window.innerHeight) + 10,
            left: window.screenX + window.outerWidth / 2 - 275,
        };
        const msLoginWindow = window.open(
            "about:blank",
            "msLoginWindow",
            "width=550,height=750,location=no,menubar=no,status=no,titlebar=no,resizable=no,top=" +
                location.top +
                ",left=" +
                location.left,
        );

        const commonOidConfig = await WebRequest.getType<any>(
            "https://login.microsoftonline.com/common/v2.0/.well-known/openid-configuration",
        );

        const authEndpoint = commonOidConfig.authorization_endpoint;
        const loc = document.location;
        const params = {
            client_id: this.state.azSettings.clientId,
            response_type: "code",
            redirect_uri: `${loc.protocol}//${loc.host}/OidHelper.html`,
            response_mode: "query",
            state: `${loc.protocol}//${loc.host}`,
            scope: "openid https://graph.microsoft.com/User.Read",
            prompt: "consent",
        };

        const query = Object.keys(params)
            .map(
                k =>
                    encodeURIComponent(k) + "=" + encodeURIComponent(params[k]),
            )
            .join("&");

        msLoginWindow.location.href = authEndpoint + "?" + query;

        let tenantInfo: AzADTenantInfo = null;
        this.props.setPageLoading?.(true);

        window["azureInfoReceiver"] = async (data: AzADTenantInfo) => {
            if (!!data) {
                delete window["azureInfoReceiver"];
                tenantInfo = data;
                msLoginWindow.close();
            }
        };

        await new Promise(resolve => {
            let pollLoginWindow = window.setInterval(function () {
                try {
                    if (msLoginWindow.closed !== false) {
                        window.clearInterval(pollLoginWindow);
                        resolve(null);
                    }
                } catch (e) {} // eslint-disable-line no-empty
            }, 500);
        });

        if (!!tenantInfo) {
            const commonOidAuth = await Datastore.postType<AzAuthResponse>(
                "api/Azure/ADApp/Authenticate",
                {
                    AuthCode: tenantInfo.code,
                    RedirectUri: `${tenantInfo.state}/OidHelper.html`,
                    Scope: "openid https://graph.microsoft.com/User.Read",
                },
            );

            const tenantDetail = await WebRequest.getType<OpenIDOrganisation>(
                "https://graph.microsoft.com/v1.0/organization/",
                {
                    headers: {
                        Authorization: `Bearer ${commonOidAuth.accessToken}`,
                    },
                },
            );

            await this.update("oidOrgName", tenantDetail.value[0].displayName);
            await this.update(
                "oidTenantID",
                JSON.stringify(tenantDetail.value[0].id),
            );
        }

        this.props.setPageLoading?.(false);
    }

    reset() {
        this.props.onDirtyChange?.(false);

        this.setState({
            school: this.props.school,
            settings: new KnownSettings(this.props.settings),
            dirty: false,
        });
    }

    async save() {
        if (await this.context.verifyCredentials()) {
            try {
                this.props.setPageLoading(true);

                await Promise.all([
                    updateKnownSettings(this.state.settings),
                    this.props.updateSchool(this.state.school),
                ]);

                this.props.onDirtyChange?.(false);

                this.setState({
                    dirty: false,
                });
            } finally {
                this.props.setPageLoading(false);
            }
        }
    }

    render() {
        return (
            <Panel>
                <PanelHeader direction={RenderOrientations.RightLeft}>
                    <Button
                        icon="save"
                        secure={true}
                        theme={ButtonThemes.Theme}
                        disabled={!this.state.dirty}
                        onClick={this.save.bind(this)}
                        tooltip="Save changes"
                        tooltipPlace="bottom"
                    />
                    <Button
                        icon="history"
                        clearanceSide={Side.Right}
                        clearanceSize={Size.Small}
                        disabled={!this.state.dirty}
                        onClick={this.reset.bind(this)}
                        tooltip="Reset to last saved"
                        tooltipPlace="bottom"
                    />
                </PanelHeader>

                <Panel>
                    <Error>
                        Note: Timetabling Solutions V9 desktop application does
                        not support authentication using SSO, V10 does.
                    </Error>
                </Panel>

                <Panel clearanceSide={Side.Top}>
                    <PanelHeader>
                        <h1>Use SAML</h1>
                    </PanelHeader>

                    <Checkbox
                        label="Enable OpenID SSO"
                        value={
                            this.state.school?.authenticationMode === "OpenID"
                        }
                        onChange={value =>
                            this.updateSchool(
                                "authenticationMode",
                                value ? "OpenID" : "TDS",
                            )
                        }
                    />
                </Panel>

                <Panel clearanceSide={Side.Top}>
                    <PanelHeader>
                        <h1>Configuration</h1>
                    </PanelHeader>

                    <Panel
                        flex={FlexDirection.Row}
                        flexSpaced={true}>
                        <DropDown
                            noType={true}
                            className="half"
                            disabled={
                                this.state.school?.authenticationMode !==
                                "OpenID"
                            }
                            value="Microsoft"
                            options={[
                                {
                                    value: "Microsoft",
                                    text: "Microsoft",
                                },
                            ]}
                        />

                        <Button
                            className="col"
                            disabled={
                                this.state.school?.authenticationMode !==
                                "OpenID"
                            }
                            theme={ButtonThemes.Theme}
                            text="Timetable Datastore OpenID Metadata"
                            tooltip="Click here to connect to your selected OpenID provider and establish an authentication connection"
                            tooltipPlace="bottom"
                            onClick={this.azureLogin.bind(this)}
                        />
                    </Panel>

                    <Panel clearanceSide={Side.Top}>
                        <PanelHeader>
                            <h2>Registered Tenant</h2>
                        </PanelHeader>

                        <Info>
                            Users registered in the following tenant will be
                            able to login using SSO:
                        </Info>

                        <Panel flex={FlexDirection.Column}>
                            <GeneralContainer flex={FlexDirection.Row}>
                                <label className="bold onesixth">
                                    Tenant Name:
                                </label>
                                <span>{this.state.settings?.oidOrgName}</span>
                            </GeneralContainer>
                            <GeneralContainer flex={FlexDirection.Row}>
                                <label className="bold onesixth">
                                    Tenant ID:
                                </label>
                                <span>{this.state.settings?.oidTenantID}</span>
                            </GeneralContainer>
                        </Panel>
                    </Panel>
                </Panel>

                <Panel clearanceSide={Side.Top}>
                    <PanelHeader>
                        <h1>Custom Domain</h1>
                    </PanelHeader>

                    <Panel clearanceSide={Side.Top}>
                        <PanelHeader>
                            <h2>Domain Name</h2>
                        </PanelHeader>

                        <Info>
                            A unique subdomain for your school, that when used
                            will automatically re-direct users to your SSO
                            platform for authentication
                        </Info>

                        <Textbox
                            value={this.state.school?.authenticationDomain}
                            onChange={value =>
                                this.updateSchool("authenticationDomain", value)
                            }
                            disabled={
                                this.state.school?.authenticationMode !==
                                "OpenID"
                            }
                        />

                        {this.state.school?.authenticationDomain && (
                            <Text clearanceSide={Side.Top}>
                                With this set up, users can go to{" "}
                                <b>
                                    {this.state.school.authenticationDomain}.
                                    {Configuration.customDomainsSuffix}
                                </b>{" "}
                                to be automatically redirected to your SSO login
                                page.
                            </Text>
                        )}
                    </Panel>
                </Panel>
            </Panel>
        );
    }
}

const mapOpenIDStateToProps = (state: IAppState) => {
    const { sso, school, settings } = state.db;
    return {
        school: school.school,
        provider: sso.provider,
        settings: settings.settings,
    };
};

const mapOpenIDDispatchToProps = dispatch => {
    return {
        setPageLoading: (value: boolean) => dispatch(setPageLoading(value)),
        initialiseSSOProviderDetails: () =>
            unwrappedDispatch(initialiseSSOProviderDetails()),
        saveSSO: (sso: SSOProvider) => unwrappedDispatch(saveSSO(sso)),
        updateSchool: (school: School) =>
            unwrappedDispatch(updateSchool(school)),
    };
};

const ConnectedSettingsOpenID = connect(
    mapOpenIDStateToProps,
    mapOpenIDDispatchToProps,
)(SettingsOpenID);

interface SettingsStorageProps {
    school: School;
    settings: Record<string, string>;
    migration: Migration;

    setPageLoading: (value: boolean) => void;
    onDirtyChange?: (dirty: boolean) => void;
    fetchSchoolMigration: () => Promise<void>;
    enactSchoolMigration: (migration: Migration) => void;
    cancelSchoolMigration: () => void;
}

interface SettingsStorageState {
    settings: KnownSettings;
    migration: Migration;
    migrationOpen: boolean;
    migrationLoading: boolean;
    migrateTiming: "now" | "later";
    error: string;
    dirty: boolean;
}

class SettingsStorage extends React.Component<
    SettingsStorageProps,
    SettingsStorageState
> {
    static contextType = AppContext;
    context!: React.ContextType<typeof AppContext>;

    state: SettingsStorageState = {
        settings: null,
        migration: null,
        migrationOpen: false,
        migrationLoading: false,
        migrateTiming: "now",
        error: null,
        dirty: false,
    };

    async componentDidMount(): Promise<void> {
        this.props.setPageLoading(true);
        await this.props.fetchSchoolMigration();
        this.props.setPageLoading(false);

        if (!!this.props.settings)
            this.setState({
                settings: new KnownSettings(this.props.settings),
                migration: this.props.migration,
            });
    }

    componentDidUpdate(prevProps: Readonly<SettingsStorageProps>): void {
        if (prevProps.settings !== this.props.settings)
            this.setState({
                settings: new KnownSettings(this.props.settings),
                migration: this.props.migration,
            });
    }

    update(name: string, value: any) {
        this.props.onDirtyChange?.(true);

        return new Promise<void>(resolve => {
            const temp = new KnownSettings(this.state.settings.data);
            temp[name] = value;
            this.setState(
                {
                    settings: temp,
                    dirty: true,
                },
                () => resolve(),
            );
        });
    }

    updateMigration(name: string, value: any) {
        const temp = { ...this.state.migration };
        temp[name] = value;
        this.setState({
            migration: temp,
        });
    }

    enableStorage(value: boolean) {
        this.update("EnableCustomAzureStorage", value);

        if (value) {
            if (!this.state.settings?.CustomAzureStorageConStr?.length)
                this.setState({
                    error: "Please provide a connection string to your Azure Storage account to enable this feature",
                });
            else
                this.validateConnection(
                    this.state.settings.CustomAzureStorageConStr,
                );
        } else {
            this.setState({
                error: null,
            });
        }
    }

    async validateConnection(connection: string) {
        await this.update("EnableCustomAzureStorage", true);
        await this.update("CustomAzureStorageConStr", connection);
        this.performValidation(connection);
    }
    performValidation = Utility.debounce(async (connection: string) => {
        try {
            if (!connection?.length)
                // eslint-disable-next-line no-throw-literal
                throw "Please provide a connection string to your Azure Storage account to enable this feature";

            const response = await Datastore.postType<boolean>(
                `api/Azure/Storage/Validate`,
                connection,
            );

            // eslint-disable-next-line no-throw-literal
            if (!response) throw "Connection string is not valid";

            this.setState({
                error: null,
            });
        } catch (error) {
            this.setState({
                error: error,
            });
        }
    }, 300);

    migrate() {
        this.setState({
            migration: {
                id: null,
                schoolId: this.props.school.id,
                type: MigrationType.Seperate,
                stage: 0,
                includeLogs: true,
                notBefore: new Date(),
                completed: null,
                cancelled: false,
                complete: false,
            },
            migrateTiming: "now",
            migrationOpen: true,
            migrationLoading: false,
        });
    }

    cancelMigrate() {
        this.setState({
            migration: null,
            migrateTiming: "now",
            migrationOpen: false,
            migrationLoading: false,
        });
    }

    async enactMigration() {
        if (await this.context.verifyCredentials()) {
            try {
                this.setState({
                    migrationLoading: true,
                });

                const migration = { ...this.state.migration };
                if (this.state.migrateTiming === "now") {
                    // add a short delay to allow the user to see the alert
                    // about being logged out before the migration starts
                    migration.notBefore = new Date().addMinutes(5);
                }

                await this.props.enactSchoolMigration(migration);

                this.setState({
                    migrationOpen: false,
                });

                if (this.state.migrateTiming === "now") {
                    await addAlert({
                        text: "Your migration has started, you will now be logged out.",
                    });

                    ApplicationState.logout();
                }
            } finally {
                this.setState({
                    migrationLoading: false,
                });
            }
        }
    }

    async cancelMigration() {
        if (await this.context.verifyCredentials()) {
            try {
                this.props.setPageLoading(true);

                await this.props.cancelSchoolMigration();
            } finally {
                this.props.setPageLoading(false);
            }
        }
    }

    reset() {
        this.props.onDirtyChange?.(false);

        this.setState({
            settings: new KnownSettings(this.props.settings),
            dirty: false,
        });
    }

    async save() {
        if (await this.context.verifyCredentials()) {
            try {
                this.props.setPageLoading(true);

                await updateKnownSettings(this.state.settings);

                this.props.onDirtyChange?.(false);

                this.setState({
                    dirty: false,
                });
            } finally {
                this.props.setPageLoading(false);
            }
        }
    }

    render() {
        return (
            <>
                <Panel>
                    <PanelHeader direction={RenderOrientations.RightLeft}>
                        <Button
                            icon="save"
                            secure={true}
                            theme={ButtonThemes.Theme}
                            disabled={!this.state.dirty || !!this.state.error}
                            onClick={this.save.bind(this)}
                            tooltip="Save changes"
                            tooltipPlace="bottom"
                        />
                        <Button
                            icon="history"
                            clearanceSide={Side.Right}
                            clearanceSize={Size.Small}
                            disabled={!this.state.dirty}
                            onClick={this.reset.bind(this)}
                            tooltip="Reset to last saved"
                            tooltipPlace="bottom"
                        />
                    </PanelHeader>

                    <Panel clearanceSide={Side.Top}>
                        <PanelHeader>
                            <h1>Use Custom Azure Storage</h1>
                        </PanelHeader>

                        <Text>
                            A school owned Azure Storage account will be used to
                            store all file data for files uploaded to the
                            project folder (including data used in imports for
                            the Staffing, Course Manager and Preferences Manager
                            products), log archives and exports from the system
                            rather Timetabling Solutions storage which has
                            capacity limits.
                        </Text>

                        <Checkbox
                            label="Enable Azure Custom Storage"
                            value={
                                this.state.settings?.EnableCustomAzureStorage
                            }
                            onChange={value => this.enableStorage(value)}
                        />

                        <Panel clearanceSide={Side.Top}>
                            <PanelHeader>
                                <h2>Azure Storage Connection String</h2>
                            </PanelHeader>

                            <Info>
                                In order to configure this you must provide a
                                connection string to your Azure Storage account,
                                the steps for obtaining this are:
                            </Info>

                            <Text>
                                <ul>
                                    <li>Go to your Microsoft Azure portal.</li>
                                    <li>
                                        Go to Azure Services
                                        <i className="inline">
                                            keyboard_arrow_right
                                        </i>
                                        Storage accounts (you may need to
                                        subscribe to use this service).
                                    </li>
                                    <li>
                                        Create a storage account of type
                                        Storage/StorageV2/BlobStorage.
                                    </li>
                                    <li>
                                        Select the storage account and go to
                                        Settings
                                        <i className="inline">
                                            keyboard_arrow_right
                                        </i>
                                        Access keys.
                                    </li>
                                    <li>
                                        Copy connection string of key1 to
                                        clipboard.
                                    </li>
                                </ul>
                            </Text>

                            <Textbox
                                value={
                                    this.state.settings
                                        ?.CustomAzureStorageConStr
                                }
                                onChange={value =>
                                    this.validateConnection(value)
                                }
                                disabled={
                                    !this.state.settings
                                        ?.EnableCustomAzureStorage
                                }
                            />

                            {this.state.error && (
                                <Error clearanceSide={Side.Top}>
                                    {this.state.error}
                                </Error>
                            )}
                        </Panel>
                    </Panel>

                    {(!this.props.migration ||
                        !this.props.migration.complete) && (
                        <Panel clearanceSide={Side.Top}>
                            <PanelHeader>
                                <h1>Database Seperation</h1>
                            </PanelHeader>

                            <Text>
                                Schools are now able to schedule a migration of
                                their data into a separate database.
                            </Text>

                            <Panel clearanceSide={Side.Top}>
                                <Info>
                                    This will move all of the schools data into
                                    a database that only contains their data,
                                    this has the benefit of improving overall
                                    performance by making loading of data
                                    faster.
                                </Info>

                                {!!this.props.migration && (
                                    <>
                                        {!this.props.migration.cancelled ? (
                                            this.props.migration.notBefore
                                                .toDate()
                                                .compare(new Date()) > 0 ? (
                                                <Text
                                                    clearanceSide={
                                                        Side.Vertical
                                                    }>
                                                    <i className="inline">
                                                        alarm
                                                    </i>
                                                    <span className="inline left-clearance">
                                                        Your school is scheduled
                                                        to have its data
                                                        migrated to a separate
                                                        database at&nbsp;
                                                        {this.props.migration.notBefore
                                                            .toDate()
                                                            .toLocaleString()}
                                                        .
                                                    </span>
                                                </Text>
                                            ) : (
                                                <Text
                                                    clearanceSide={
                                                        Side.Vertical
                                                    }>
                                                    <i className="inline">
                                                        cached
                                                    </i>
                                                    <span className="inline left-clearance">
                                                        Your schools data is
                                                        currently being migrated
                                                        to a separate database.
                                                    </span>
                                                </Text>
                                            )
                                        ) : null}

                                        {!!this.props.migration.cancelled && (
                                            <Text clearanceSide={Side.Vertical}>
                                                <i className="inline">close</i>
                                                <span className="inline left-clearance">
                                                    You cancelled your
                                                    migration.
                                                </span>
                                            </Text>
                                        )}

                                        {!!this.props.migration.completed &&
                                            !this.props.migration.complete && (
                                                <Error
                                                    clearanceSide={
                                                        Side.Vertical
                                                    }>
                                                    <i className="inline">
                                                        close
                                                    </i>
                                                    <span className="inline left-clearance">
                                                        Your migration failed to
                                                        complete, please contact
                                                        Timetabling Solutions
                                                        support to allow us to
                                                        investigate why it
                                                        failed before retrying.
                                                    </span>
                                                </Error>
                                            )}
                                    </>
                                )}

                                {!this.props.migration ||
                                this.props.migration?.cancelled ||
                                (!!this.props.migration?.completed &&
                                    !this.props.migration?.complete) ? (
                                    <Button
                                        icon="cached"
                                        text="Migrate Database"
                                        clearanceSide={Side.Top}
                                        onClick={this.migrate.bind(this)}
                                    />
                                ) : (
                                    <Button
                                        icon="close"
                                        text="Cancel Migration"
                                        clearanceSide={Side.Top}
                                        onClick={this.cancelMigration.bind(
                                            this,
                                        )}
                                    />
                                )}
                            </Panel>
                        </Panel>
                    )}

                    {!!this.props.migration && !!this.props.migration.complete && (
                        <Panel clearanceSide={Side.Top}>
                            <PanelHeader>
                                <h1>Database Separation</h1>
                            </PanelHeader>
                        </Panel>
                    )}
                </Panel>

                <Dialog
                    showDialog={this.state.migrationOpen}
                    dialogIcon="cached"
                    dialogTitle="Migrate Database"
                    width={700}>
                    <DialogContent>
                        <Panel>
                            <PanelHeader>
                                <h4>Migrate to separate database</h4>
                            </PanelHeader>

                            <Text>
                                <p>
                                    The process of migrating your data into your
                                    own database could take a significant amount
                                    of time and we recommend running this at a
                                    time when your school won't need access to
                                    the system for at least 48 hours.
                                </p>
                                <p>
                                    During this process no one from your school
                                    will be able to access the environment,
                                    scheduled LISS and SIF integrations will
                                    continue to run during this time.
                                </p>
                                <p>
                                    All global administrators of your school
                                    will receive an email once the migration is
                                    complete, or if there is a problem with the
                                    migration for any reason.
                                </p>
                                <p>
                                    If there is a problem with the migration it
                                    will be rolled back and we will record the
                                    cause of the problem in order to resolve it.
                                    Once the problem is resolved you can then
                                    re-attempt the migration.
                                </p>
                            </Text>
                        </Panel>

                        <Panel clearanceSide={Side.Top}>
                            <Checkbox
                                label="Include logs"
                                value={this.state.migration?.includeLogs}
                                onChange={value =>
                                    this.updateMigration("includeLogs", value)
                                }
                            />

                            <Info clearanceSide={Side.Top}>
                                Unchecking the "Include Logs" option can
                                significantly decrease the time it takes to
                                migrate your data. If you choose not to migrate
                                your logs then you will not see historical edit
                                records from before the migration was performed.
                            </Info>
                        </Panel>

                        <Panel clearanceSide={Side.Top}>
                            <RadioList
                                display={RenderOrientations.Vertical}
                                value={this.state.migrateTiming}
                                options={[
                                    {
                                        value: "now",
                                        text: "Migrate now",
                                    },
                                    {
                                        value: "later",
                                        text: "Schedule Migration",
                                    },
                                ]}
                                onChange={value =>
                                    this.setState({ migrateTiming: value })
                                }
                            />

                            {this.state.migrateTiming === "later" && (
                                <GeneralContainer
                                    clearanceSide={Side.Left}
                                    clearanceSize={Size.Large}
                                    flex={FlexDirection.Row}
                                    flexSpaced={true}>
                                    <DatePicker
                                        value={this.state.migration?.notBefore}
                                        minDate={new Date()}
                                        onChange={date =>
                                            this.updateMigration(
                                                "notBefore",
                                                date.mergeTime(
                                                    this.state.migration
                                                        ?.notBefore,
                                                ),
                                            )
                                        }
                                    />

                                    <TimePicker
                                        value={this.state.migration?.notBefore}
                                        minTime={new Date()}
                                        onChange={date =>
                                            this.updateMigration(
                                                "notBefore",
                                                date.mergeDate(
                                                    this.state.migration
                                                        ?.notBefore,
                                                ),
                                            )
                                        }
                                    />
                                </GeneralContainer>
                            )}
                        </Panel>
                    </DialogContent>
                    <DialogFooter>
                        <DialogCancel onClick={this.cancelMigrate.bind(this)} />
                        <DialogOK
                            text="Migrate Database"
                            onClick={this.enactMigration.bind(this)}
                        />
                    </DialogFooter>
                </Dialog>
            </>
        );
    }
}

const mapStorageStateToProps = (state: IAppState) => {
    const { school, settings } = state.db;
    return {
        school: school.school,
        settings: settings.settings,
        migration: school.migration,
    };
};

const mapStorageDispatchToProps = dispatch => {
    return {
        setPageLoading: (value: boolean) => dispatch(setPageLoading(value)),
        fetchSchoolMigration: () => unwrappedDispatch(fetchSchoolMigration()),
        enactSchoolMigration: (migration: Migration) =>
            unwrappedDispatch(enactSchoolMigration(migration)),
        cancelSchoolMigration: () => unwrappedDispatch(cancelSchoolMigration()),
    };
};

const ConnectedSettingsStorage = connect(
    mapStorageStateToProps,
    mapStorageDispatchToProps,
)(SettingsStorage);

interface SettingsDashboardProps {
    users: User[];
    dashboard: Dashboard;
    dashboardBanner: string;
    school: School;
    settings: Record<string, string>;

    setPageLoading: (value: boolean) => void;
    onDirtyChange?: (dirty: boolean) => void;
    initialiseDashboardDetails: () => Promise<void>;
    getDashboardUpload: (payload: string) => Promise<string>;
    saveDashboardUpload: (payload: {
        token: string;
        data: string;
    }) => Promise<void>;
    removeDashboardUpload: (payload: string) => Promise<void>;
    saveDashboard: (payload: Dashboard) => Promise<void>;
}

interface SettingsDashboardState {
    dashboard: Dashboard;
    bannerImage: string;
    bannerImageDisplay: string;
    newBanner: boolean;
    school: School;
    settings: KnownSettings;
    dirty: boolean;
    azSettings?: AzImportSettings;
    azMappings?: AzImportMapping;
}

class SettingsDashboard extends React.Component<
    SettingsDashboardProps,
    SettingsDashboardState
> {
    static contextType = AppContext;
    context!: React.ContextType<typeof AppContext>;

    state: SettingsDashboardState = {
        dashboard: this.getDefaultDashboard(),
        bannerImage: null,
        bannerImageDisplay: null,
        newBanner: false,
        school: null,
        settings: null,
        dirty: false,
    };

    async componentDidMount(): Promise<void> {
        this.props.setPageLoading(true);
        await this.props.initialiseDashboardDetails();
        this.props.setPageLoading(false);

        if (!!this.props.settings) {
            const dash =
                structuredClone(this.props.dashboard) ??
                this.getDefaultDashboard();
            dash.widgets?.sort(Utility.getSorter(["name"]));
            this.setState({
                dashboard: dash,
                bannerImage: this.props.dashboardBanner,
                bannerImageDisplay: await this.getDisplayBackground(
                    dash?.banner?.style,
                    this.props.dashboardBanner,
                ),
                school: this.props.school,
                settings: new KnownSettings(this.props.settings),
            });
        }
    }

    async componentDidUpdate(
        prevProps: Readonly<SettingsDashboardProps>,
    ): Promise<void> {
        if (
            prevProps.school !== this.props.school ||
            prevProps.settings !== this.props.settings
        )
            this.setState({
                school: this.props.school,
                settings: new KnownSettings(this.props.settings),
            });
        let dash = this.state.dashboard;
        if (prevProps.dashboard !== this.props.dashboard) {
            dash =
                structuredClone(this.props.dashboard) ??
                this.getDefaultDashboard();
            dash.widgets.sort(Utility.getSorter(["name"]));
            this.setState({
                dashboard: dash,
            });
        }
        if (prevProps.dashboardBanner !== this.props.dashboardBanner) {
            this.setState({
                bannerImage: this.props.dashboardBanner,
                bannerImageDisplay: await this.getDisplayBackground(
                    dash?.banner?.style,
                    this.props.dashboardBanner,
                ),
            });
        }
    }

    getDefaultDashboard() {
        return {
            banner: {
                enabled: false,
                style: "cover",
                colour: "#1e3283",
                token: Utility.newGuid().toString(),
            },
            widgets: [
                this.getNewWidget(
                    DashboardWidgetType.Contacts,
                    "Staff Contact List",
                    "Displays contact details users including student enrolled class teachers",
                ),
                this.getNewWidget(
                    DashboardWidgetType.Activities,
                    "Activities",
                    "Displays a current list of activities",
                ),
                this.getNewWidget(
                    DashboardWidgetType.Files,
                    "School Newsletters",
                    "Allows all students to view uploaded files such as School Newsletters",
                ),
                this.getNewWidget(
                    DashboardWidgetType.Urls,
                    "Helpful Links",
                    "Provides a list of helpful links (urls) for students",
                ),
            ],
        } as Dashboard;
    }

    getNewWidget(type: DashboardWidgetType, name: string, description: string) {
        return {
            id: 0,
            type: type,
            name: name,
            description: description,
            enabled: false,
            contacts: [],
            files: [],
            urls: [],
        } as DashboardWidget;
    }

    update(name: string, value: any) {
        this.props.onDirtyChange?.(true);

        return new Promise<void>(resolve => {
            const temp = new KnownSettings(this.state.settings.data);
            temp[name] = value;
            this.setState(
                {
                    settings: temp,
                    dirty: true,
                },
                () => resolve(),
            );
        });
    }

    async updateDashboard(name: string, value: any) {
        const temp = { ...this.state.dashboard };

        const styleUpdate =
            name === "banner" &&
            value.style !== this.state.dashboard.banner.style;

        temp[name] = value;

        this.props.onDirtyChange?.(true);

        this.setState({
            dashboard: temp,
            bannerImageDisplay: styleUpdate
                ? await this.getDisplayBackground(
                      value.style,
                      this.state.bannerImage,
                  )
                : this.state.bannerImageDisplay,
            dirty: true,
        });
    }

    async updateWidget(index: number, name: string, value: any) {
        const temp = { ...this.state.dashboard };
        temp.widgets[index][name] = value;
        this.setState({
            dashboard: temp,
            dirty: true,
        });
    }

    async addWidgetItem(index: number, widget: DashboardWidget) {
        switch (widget.type) {
            case DashboardWidgetType.Contacts:
                const contact = await this.addContact();
                if (!!contact)
                    await this.updateWidget(index, "contacts", [
                        ...(widget.contacts ?? []),
                        contact,
                    ]);
                break;
            case DashboardWidgetType.Files:
                const files = await this.addFile();
                await this.updateWidget(index, "files", [
                    ...(widget.files ?? []),
                    ...files,
                ]);
                break;
            case DashboardWidgetType.Urls:
                const url = await this.addUrl();
                if (!!url)
                    await this.updateWidget(index, "urls", [
                        ...(widget.urls ?? []),
                        url,
                    ]);
                break;
        }
    }

    removeWidgetItem(
        widgetIndex: number,
        itemIndex: number,
        widget: DashboardWidget,
    ) {
        switch (widget.type) {
            case DashboardWidgetType.Contacts:
                this.updateWidget(widgetIndex, "contacts", [
                    ...(widget.contacts ?? []).filter(
                        (_, index) => index !== itemIndex,
                    ),
                ]);
                break;
            case DashboardWidgetType.Files:
                const files = widget.files ?? [];
                const file = { ...files[itemIndex], removed: true };

                this.updateWidget(widgetIndex, "files", [
                    ...files
                        .filter((_, index) => index !== itemIndex)
                        .concat([file]),
                ]);
                break;
            case DashboardWidgetType.Urls:
                this.updateWidget(widgetIndex, "urls", [
                    ...(widget.urls ?? []).filter(
                        (_, index) => index !== itemIndex,
                    ),
                ]);
                break;
        }

        ReactTooltip.hide();
    }

    async addContact() {
        const result = await addAlert({
            type: AlertTypes.Prompt,
            icon: "edit",
            title: "Add Contact",
            prompts: [
                {
                    type: "text",
                    display: "Contact Type Label",
                    value: "Contact",
                },
                {
                    type: "select",
                    display: "Contact",
                    value: this.props.users[0]?.personID,
                    options: this.props.users.map(user => ({
                        value: user.personID,
                        text: user.fullName,
                    })),
                },
            ],
            okText: "Add",
        });

        if (result.response)
            return {
                id: 0,
                type: result.promptResults[0].value,
                personID: result.promptResults[1].value,
            };
        else return null;
    }

    async addUrl() {
        const add = async (
            message?: string,
            url?: string,
            urlName?: string,
        ) => {
            const result = await addAlert({
                type: AlertTypes.Prompt,
                icon: "edit",
                title: "Add URL",
                text: message,
                prompts: [
                    {
                        type: "text",
                        display: "Url Name",
                        value: urlName ?? "",
                    },
                    {
                        type: "text",
                        display: "Url Address",
                        value: url ?? "",
                    },
                ],
                okText: "Add",
            });

            if (result.response) {
                try {
                    this.props.setPageLoading(true);

                    const urlCheck = await this.checkUrl(
                        result.promptResults[1].value,
                    );

                    if (!urlCheck) {
                        return {
                            id: 0,
                            urlName: result.promptResults[0].value,
                            url: result.promptResults[1].value,
                            urlDescription: "",
                        };
                    } else {
                        this.props.setPageLoading(false);

                        return await add(
                            `<i class="inline red">error</i>&nbsp;<span class="inline error wrappable">${urlCheck}</span>`,
                            result.promptResults[1].value,
                            result.promptResults[0].value,
                        );
                    }
                } catch {
                    return null;
                } finally {
                    this.props.setPageLoading(false);
                }
            }
        };

        return await add();
    }

    async checkUrl(url: string) {
        if (!Utility.isValidHttpUrl(url)) {
            return "Please use a valid Url";
        }

        const checkResults = await checkUrl(url);
        if (checkResults.length > 0) {
            return `${getCheckResultMessage(
                checkResults,
            )}, please use an alternate URL.`;
        }

        return null;
    }

    async addFile() {
        const files = await Utility.uploadFile(".pdf");

        return files.map(file => ({
            id: 0,
            fileName: file.name,
            displayName: file.name,
            storageToken: Utility.newGuid().toString(),
            data: file.data,
        }));
    }

    async downloadFile(file: DashboardWidgetFile) {
        if (!file.data) {
            try {
                this.props.setPageLoading(true);
                const data = await this.props.getDashboardUpload(
                    file.storageToken,
                );
                if (!!data) file.data = data;
                // eslint-disable-next-line no-throw-literal
                else throw "Error downloading file";
            } catch {
                addAlert({
                    type: AlertTypes.Alert,
                    title: "Error",
                    text: "There was an error downloading the file, please try again later.",
                    icon: "error",
                });
            } finally {
                this.props.setPageLoading(false);
            }
        }

        if (!!file.data) file.data.download(file.fileName);
    }

    async setBackground() {
        const file = await Utility.uploadFile(".png,.jpeg,.jpg,.svg", 1);

        if (!file.length) return;

        const imageData = await Utility.cropImage(file[0].data, {
            width: 2200,
            height: 360,
        });

        await this.updateDashboard("banner", {
            ...this.state.dashboard.banner,
            enabled: true,
        });

        this.props.onDirtyChange?.(true);

        this.setState({
            newBanner: true,
            bannerImage: imageData,
            bannerImageDisplay: await this.getDisplayBackground(
                this.state.dashboard.banner.style,
                imageData,
            ),
            dirty: true,
        });
    }

    async removeBackground() {
        await this.updateDashboard("banner", {
            ...this.state.dashboard.banner,
            enabled: false,
        });

        this.props.onDirtyChange?.(true);

        this.setState({
            newBanner: false,
            bannerImage: null,
            bannerImageDisplay: null,
            dirty: true,
        });
    }

    async getDisplayBackground(style: string, imageData: string) {
        return style === "cover"
            ? imageData
            : await Utility.cropImage(imageData, {
                  width: 2200,
                  height: 360,
                  radius: 20,
              });
    }

    getBanner() {
        if (this.state.bannerImageDisplay)
            return this.state.bannerImageDisplay.toDataUrl();

        return null;
    }

    reset() {
        this.props.onDirtyChange?.(false);

        this.setState({
            school: this.props.school,
            dashboard: this.props.dashboard ?? this.getDefaultDashboard(),
            settings: new KnownSettings(this.props.settings),
            newBanner: false,
            dirty: false,
        });
    }

    async save() {
        if (await this.context.verifyCredentials()) {
            try {
                this.props.setPageLoading(true);

                const dashboard = structuredClone(this.state.dashboard);

                if (!dashboard.banner.enabled)
                    await this.props.removeDashboardUpload(
                        dashboard.banner.token,
                    );

                const waits = [];
                const adds = [];
                for (const widget of dashboard.widgets ?? []) {
                    for (const file of widget.files ?? []) {
                        const data = file.data;
                        if (!data && !!file.removed)
                            waits.push(
                                this.props.removeDashboardUpload(
                                    file.storageToken,
                                ),
                            );
                        else if (data && !file.removed)
                            adds.push(() =>
                                this.props.saveDashboardUpload({
                                    token: file.storageToken,
                                    data: data,
                                }),
                            );
                    }
                }

                if (waits.length) await Promise.all(waits);

                for (const widget of dashboard.widgets ?? [])
                    widget.files = widget.files
                        ?.filter(file => !file.removed)
                        .map(file => {
                            delete file.data;
                            delete file.removed;
                            return file;
                        });

                await this.props.saveDashboard(dashboard);

                await Promise.all(adds.map(add => add()));

                if (this.state.newBanner && !!this.state.bannerImage)
                    await this.props.saveDashboardUpload({
                        token: dashboard.banner.token,
                        data: this.state.bannerImage,
                    });

                await this.props.initialiseDashboardDetails();

                this.props.onDirtyChange?.(false);

                this.setState({
                    dirty: false,
                });
            } finally {
                this.props.setPageLoading(false);
            }
        }
    }

    render() {
        return (
            <Panel>
                <PanelHeader direction={RenderOrientations.RightLeft}>
                    <Button
                        icon="save"
                        secure={true}
                        theme={ButtonThemes.Theme}
                        disabled={!this.state.dirty}
                        onClick={this.save.bind(this)}
                        tooltip="Save changes"
                        tooltipPlace="bottom"
                    />
                    <Button
                        icon="history"
                        clearanceSide={Side.Right}
                        clearanceSize={Size.Small}
                        disabled={!this.state.dirty}
                        onClick={this.reset.bind(this)}
                        tooltip="Reset to last saved"
                        tooltipPlace="bottom"
                    />
                </PanelHeader>

                <Panel clearanceSide={Side.Top}>
                    <PanelHeader>
                        <h1>School Banner</h1>
                    </PanelHeader>

                    <Info>
                        Displays the uploaded School Banner for all students in
                        their Management Portal
                    </Info>

                    <Panel
                        clearanceSide={Side.Top}
                        style={{
                            minHeight: "75px",
                            height: this.state.bannerImage ? "220px" : null,
                            backgroundPosition: "center top",
                            backgroundRepeat: "no-repeat",
                            backgroundSize: "cover",
                            backgroundImage:
                                this.state.dashboard?.banner?.style === "cover"
                                    ? this.state.bannerImage
                                        ? `url(${this.getBanner()})`
                                        : undefined
                                    : undefined,
                            backgroundColor: this.state.bannerImage
                                ? this.state.dashboard?.banner?.colour
                                : undefined,
                        }}>
                        <Button
                            style={{
                                position: "absolute",
                                bottom: "12px",
                                left: "12px",
                            }}
                            heightSize={Size.Large}
                            theme={ButtonThemes.Theme}
                            text="Choose Banner Image"
                            onClick={this.setBackground.bind(this)}
                        />

                        {this.state.bannerImage && (
                            <>
                                {this.state.dashboard?.banner?.style ===
                                    "contain" && (
                                    <GeneralContainer
                                        style={{
                                            height: this.state.bannerImage
                                                ? "200px"
                                                : null,
                                            backgroundPosition: "center top",
                                            backgroundRepeat: "no-repeat",
                                            backgroundSize: "contain",
                                            backgroundImage: this.state
                                                .bannerImage
                                                ? `url(${this.getBanner()})`
                                                : undefined,
                                        }}
                                    />
                                )}
                                <GeneralContainer
                                    bufferSide={Side.All}
                                    style={{
                                        position: "absolute",
                                        bottom: "12px",
                                        left: "300px",
                                        width: "550px",
                                        background: "var(--main-hover-mask)",
                                        backdropFilter: "blur(10px)",
                                        borderRadius: "10px",
                                    }}>
                                    <InputForm
                                        binding={this.state.dashboard?.banner}
                                        display={RenderOrientations.Horizontal}
                                        fieldDisplay={
                                            RenderOrientations.Horizontal
                                        }
                                        onChange={value =>
                                            this.updateDashboard(
                                                "banner",
                                                value,
                                            )
                                        }>
                                        <InputFormField
                                            type={InputFieldTypes.Radio}
                                            label="Banner Style"
                                            labelBold={true}
                                            field="style"
                                            options={[
                                                {
                                                    text: "Contain",
                                                    value: "contain",
                                                },
                                                {
                                                    text: "Cover",
                                                    value: "cover",
                                                },
                                            ]}
                                        />
                                        <InputFormField
                                            type={InputFieldTypes.Colour}
                                            label="Background Colour"
                                            labelBold={true}
                                            field="colour"
                                        />
                                    </InputForm>
                                </GeneralContainer>

                                <Button
                                    className="inline"
                                    style={{
                                        position: "absolute",
                                        top: "10px",
                                        right: "10px",
                                    }}
                                    icon="close"
                                    iconStyle={IconStyles.Red}
                                    tooltip="Remove banner"
                                    tooltipPlace="left"
                                    onClick={this.removeBackground.bind(this)}
                                />
                            </>
                        )}
                    </Panel>
                </Panel>

                {this.state.dashboard?.widgets?.map((widget, index) => (
                    <Panel
                        key={index}
                        clearanceSide={Side.Top}>
                        <PanelHeader>
                            <h1>{widget.name}</h1>

                            <Spacer />

                            <Checkbox
                                label="Enabled"
                                value={widget.enabled}
                                onChange={value =>
                                    this.updateWidget(index, "enabled", value)
                                }
                            />
                        </PanelHeader>

                        <Info>{widget.description}</Info>

                        <Panel clearanceSide={Side.Top}>
                            <InputForm
                                binding={widget}
                                fieldDisplay={RenderOrientations.Horizontal}>
                                <InputFormField
                                    field="name"
                                    labelBold={true}
                                    label="Display Name"
                                    type={InputFieldTypes.Text}
                                    disabled={!widget.enabled}
                                    onChange={value =>
                                        this.updateWidget(index, "name", value)
                                    }
                                />
                            </InputForm>
                        </Panel>

                        {widget.type !== DashboardWidgetType.Activities && (
                            <>
                                <Panel
                                    clearanceSide={Side.Top}
                                    flex={FlexDirection.Column}
                                    flexSpaced={true}>
                                    <PanelHeader
                                        direction={
                                            RenderOrientations.RightLeft
                                        }>
                                        <Button
                                            icon="add"
                                            inline={true}
                                            tooltip={`Add new ${String(
                                                widget.type,
                                            ).toLowerCase()}`}
                                            tooltipPlace="right"
                                            disabled={!widget.enabled}
                                            onClick={() =>
                                                this.addWidgetItem(
                                                    index,
                                                    widget,
                                                )
                                            }
                                        />
                                    </PanelHeader>

                                    {!!widget.contacts?.length &&
                                        widget.contacts.map(
                                            (contact, contactIndex) => (
                                                <Panel
                                                    key={contactIndex}
                                                    flex={FlexDirection.Row}
                                                    className="list-item">
                                                    <Text inline={true}>
                                                        <b>{contact.type}:</b>
                                                        &nbsp;
                                                        {
                                                            this.props.users.find(
                                                                user =>
                                                                    user.personID ===
                                                                    contact.personID,
                                                            )?.fullName
                                                        }
                                                    </Text>

                                                    <Spacer />

                                                    <Button
                                                        inline={true}
                                                        icon="close"
                                                        iconStyle={
                                                            IconStyles.Red
                                                        }
                                                        className="remove"
                                                        tooltip="Remove this contact"
                                                        tooltipPlace="left"
                                                        onClick={() =>
                                                            this.removeWidgetItem(
                                                                index,
                                                                contactIndex,
                                                                widget,
                                                            )
                                                        }
                                                    />
                                                </Panel>
                                            ),
                                        )}

                                    {!!widget.urls?.length &&
                                        widget.urls.map((url, urlIndex) => (
                                            <Panel
                                                key={urlIndex}
                                                flex={FlexDirection.Row}
                                                className="list-item">
                                                <a
                                                    href={url.url}
                                                    target="_blank"
                                                    rel="noreferrer">
                                                    {url.urlName}
                                                </a>

                                                <Spacer />

                                                <Button
                                                    inline={true}
                                                    icon="close"
                                                    iconStyle={IconStyles.Red}
                                                    className="remove"
                                                    tooltip="Remove this URL"
                                                    tooltipPlace="left"
                                                    onClick={() =>
                                                        this.removeWidgetItem(
                                                            index,
                                                            urlIndex,
                                                            widget,
                                                        )
                                                    }
                                                />
                                            </Panel>
                                        ))}

                                    {!!widget.files?.filter(
                                        file => !file.removed,
                                    ).length &&
                                        widget.files.map((file, fileIndex) =>
                                            file.removed ? null : (
                                                <Panel
                                                    key={fileIndex}
                                                    flex={FlexDirection.Row}
                                                    className="list-item">
                                                    <Button
                                                        text={file.displayName}
                                                        inline={true}
                                                        theme={
                                                            ButtonThemes.Link
                                                        }
                                                        onClick={this.downloadFile.bind(
                                                            this,
                                                            file,
                                                        )}
                                                    />

                                                    <Spacer />

                                                    <Button
                                                        inline={true}
                                                        icon="close"
                                                        iconStyle={
                                                            IconStyles.Red
                                                        }
                                                        className="remove"
                                                        tooltip="Remove this newsletter"
                                                        tooltipPlace="left"
                                                        onClick={() =>
                                                            this.removeWidgetItem(
                                                                index,
                                                                fileIndex,
                                                                widget,
                                                            )
                                                        }
                                                    />
                                                </Panel>
                                            ),
                                        )}
                                </Panel>
                            </>
                        )}
                    </Panel>
                ))}
            </Panel>
        );
    }
}

const mapDashboardStateToProps = (state: IAppState) => {
    const { dashboard, school, settings, user } = state.db;
    return {
        users: user.allUsers,
        dashboard: dashboard.dashboard,
        dashboardBanner: dashboard.dashboardBanner,
        school: school.school,
        settings: settings.settings,
    };
};

const mapDashboardDispatchToProps = dispatch => {
    return {
        setPageLoading: (value: boolean) => dispatch(setPageLoading(value)),
        initialiseDashboardDetails: () =>
            unwrappedDispatch(initialiseDashboardDetails()),
        getDashboardUpload: (payload: string) =>
            unwrappedDispatch(getDashboardUpload(payload)),
        saveDashboardUpload: (payload: { token: string; data: string }) =>
            unwrappedDispatch(saveDashboardUpload(payload)),
        removeDashboardUpload: (payload: string) =>
            unwrappedDispatch(removeDashboardUpload(payload)),
        saveDashboard: (payload: Dashboard) =>
            unwrappedDispatch(saveDashboard(payload)),
    };
};

const ConnectedSettingsDashboard = connect(
    mapDashboardStateToProps,
    mapDashboardDispatchToProps,
)(SettingsDashboard);

interface SettingsDOProps {
    user: User;
    school: School;
    settings: Record<string, string>;

    setPageLoading: (value: boolean) => void;
    onDirtyChange?: (dirty: boolean) => void;
}

interface SettingsDOState {
    settings: KnownSettings;
    notifications: "none" | "dayOf";
    error: string;
    dirty: boolean;
}

class SettingsDO extends React.Component<SettingsDOProps, SettingsDOState> {
    static contextType = AppContext;
    context!: React.ContextType<typeof AppContext>;

    state: SettingsDOState = {
        settings: null,
        notifications: "none",
        error: null,
        dirty: false,
    };

    async componentDidMount(): Promise<void> {
        if (!!this.props.settings) {
            const known = new KnownSettings(this.props.settings);
            this.setState({
                settings: known,
                notifications: !!known.dONotifications ? "dayOf" : "none",
            });
        }
    }

    componentDidUpdate(prevProps: Readonly<SettingsDOProps>): void {
        if (prevProps.settings !== this.props.settings) {
            const known = new KnownSettings(this.props.settings);
            this.setState({
                settings: known,
                notifications: !!known.dONotifications ? "dayOf" : "none",
            });
        }
    }

    getNotificationTime() {
        return !!this.state.settings?.dONotifications
            ? this.state.settings?.dONotifications?.toDate()
            : undefined;
    }

    update(name: string, value: any) {
        this.props.onDirtyChange?.(true);

        return new Promise<void>(resolve => {
            const temp = new KnownSettings(this.state.settings.data);
            temp[name] = value;
            this.setState(
                {
                    settings: temp,
                    dirty: true,
                },
                () => resolve(),
            );
        });
    }

    toggleNotifications(value: "none" | "dayOf") {
        this.props.onDirtyChange?.(true);

        this.setState({
            notifications: value,
            dirty: true,
        });

        this.update(
            "dONotifications",
            value === "none" ? "" : new Date().dateOnly(),
        );
    }

    async updateDestructive(value: any) {
        if (value === true) {
            const result = await addAlert({
                type: AlertTypes.Confirm,
                icon: "warning",
                styleColor: "yellow",
                title: "Assume Liability",
                text: "By enabling this setting you assume liability for any data removed by the timetable publish process.",
                okText: "I assume liability",
            });

            if (result.response) {
                await this.update("dOConfiguration_DestructivePublish", true);
                await this.update(
                    "dOConfiguration_DestructivePublishUser",
                    `${this.props.user.firstName} ${this.props.user.lastName} (${this.props.user.username})`,
                );
                await this.update(
                    "dOConfiguration_DestructivePublishUserID",
                    String(this.props.user.id),
                );
                await this.update(
                    "dOConfiguration_DestructivePublishDate",
                    new Date().toISOString(),
                );
            }
        } else {
            this.update("dOConfiguration_DestructivePublish", false);
        }
    }

    reset() {
        this.props.onDirtyChange?.(false);

        this.setState({
            settings: new KnownSettings(this.props.settings),
            dirty: false,
        });
    }

    async save() {
        if (await this.context.verifyCredentials()) {
            try {
                this.props.setPageLoading(true);

                await updateKnownSettings(this.state.settings);

                this.props.onDirtyChange?.(false);

                this.setState({
                    dirty: false,
                });
            } finally {
                this.props.setPageLoading(false);
            }
        }
    }

    render() {
        return (
            <>
                <Panel>
                    <PanelHeader direction={RenderOrientations.RightLeft}>
                        <Button
                            icon="save"
                            secure={true}
                            theme={ButtonThemes.Theme}
                            disabled={!this.state.dirty || !!this.state.error}
                            onClick={this.save.bind(this)}
                            tooltip="Save changes"
                            tooltipPlace="bottom"
                        />
                        <Button
                            icon="history"
                            clearanceSide={Side.Right}
                            clearanceSize={Size.Small}
                            disabled={!this.state.dirty}
                            onClick={this.reset.bind(this)}
                            tooltip="Reset to last saved"
                            tooltipPlace="bottom"
                        />
                    </PanelHeader>

                    <Panel clearanceSide={Side.Top}>
                        <PanelHeader>
                            <h1>Daily Organiser</h1>
                        </PanelHeader>

                        <Info>
                            These settings allow you to automatically send
                            teachers with a replacement class a notification at
                            a specified time.
                        </Info>

                        <Panel clearanceSide={Side.Top}>
                            <RadioList
                                display={RenderOrientations.Vertical}
                                value={this.state.notifications}
                                onChange={this.toggleNotifications.bind(this)}
                                options={[
                                    {
                                        text: "No Notifications",
                                        value: "none",
                                    },
                                    {
                                        text: "Notifications sent at",
                                        value: "dayOf",
                                    },
                                ]}
                            />

                            <GeneralContainer
                                bufferSide={Side.Left}
                                bufferSize={Size.XtraLarge}
                                flex={FlexDirection.Row}
                                flexSpaced={true}>
                                <TimePicker
                                    disabled={
                                        this.state.notifications !== "dayOf"
                                    }
                                    value={this.getNotificationTime()}
                                    onChange={value =>
                                        this.update("dONotifications", value)
                                    }
                                />

                                <Checkbox
                                    label="Show student attendance list"
                                    disabled={
                                        this.state.notifications !== "dayOf"
                                    }
                                    value={
                                        this.state.settings
                                            ?.dONotifications_StudentLists
                                    }
                                    onChange={value =>
                                        this.update(
                                            "dONotifications_StudentLists",
                                            value,
                                        )
                                    }
                                />

                                <Checkbox
                                    label="Show teacher lesson cancellations"
                                    disabled={
                                        this.state.notifications !== "dayOf"
                                    }
                                    value={
                                        this.state.settings
                                            ?.dONotifications_Cancellations
                                    }
                                    onChange={value =>
                                        this.update(
                                            "dONotifications_Cancellations",
                                            value,
                                        )
                                    }
                                />

                                <Checkbox
                                    label="Show composite classes separately"
                                    disabled={
                                        this.state.notifications !== "dayOf"
                                    }
                                    value={
                                        this.state.settings
                                            ?.dONotifications_Composites
                                    }
                                    onChange={value =>
                                        this.update(
                                            "dONotifications_Composites",
                                            value,
                                        )
                                    }
                                />
                            </GeneralContainer>
                        </Panel>
                    </Panel>

                    <Panel clearanceSide={Side.Top}>
                        <PanelHeader>
                            <h1>Daily Organiser Timetable Publishing</h1>
                        </PanelHeader>

                        <Panel>
                            <Info>
                                <p>
                                    Enabling this feature and then publishing a
                                    timetable with missing period or yard duty
                                    codes will <b>permanently remove</b> data
                                    from your Daily Organiser file.
                                </p>
                                <p>
                                    Once a timetable publish completes we will
                                    process the dates the new timetable applies
                                    to, where a period or yard duty code doesn't
                                    exist anymore we will remove any associated
                                    data from Daily Organiser.
                                </p>
                                <p>
                                    This does not affect any data where period
                                    and yard duty codes remain the same. If you
                                    have no Daily Organiser data entered for the
                                    dates the new timetable will apply to then
                                    there is no impact.
                                </p>
                                <p>
                                    Please keep in mind that the removal of any
                                    data as a result of this process{" "}
                                    <b>cannot be undone</b>. For more
                                    information please see our support
                                    documentation&nbsp;
                                    <a
                                        className="single-line"
                                        href="https://timetabling.elevio.help/en/articles/759-cannot-publish-a-timetable-file"
                                        target="_blank"
                                        rel="noopener noreferrer">
                                        here
                                    </a>
                                    .
                                </p>
                            </Info>

                            <Checkbox
                                className="error"
                                label="Enable Period Structure Realignment"
                                value={
                                    this.state.settings
                                        ?.dOConfiguration_DestructivePublish
                                }
                                onChange={value =>
                                    this.updateDestructive(value)
                                }
                            />
                        </Panel>
                    </Panel>
                </Panel>
            </>
        );
    }
}

const mapDOStateToProps = (state: IAppState) => {
    const { school, settings } = state.db;
    const { session } = state.session;
    return {
        user: session.user,
        school: school.school,
        settings: settings.settings,
    };
};

const mapDODispatchToProps = dispatch => {
    return {
        setPageLoading: (value: boolean) => dispatch(setPageLoading(value)),
    };
};

const ConnectedSettingsDO = connect(
    mapDOStateToProps,
    mapDODispatchToProps,
)(SettingsDO);

interface SettingsPortalsProps {
    user: User;
    school: School;
    settings: Record<string, string>;

    setPageLoading: (value: boolean) => void;
    onDirtyChange?: (dirty: boolean) => void;
}

interface SettingsPortalsState {
    settings: KnownSettings;
    teacherValue: "current" | "all" | "custom";
    studentValue: "current" | "all" | "custom";
    error: string;
    dirty: boolean;
}

class SettingsPortals extends React.Component<
    SettingsPortalsProps,
    SettingsPortalsState
> {
    static contextType = AppContext;
    context!: React.ContextType<typeof AppContext>;

    state: SettingsPortalsState = {
        settings: null,
        teacherValue: "current",
        studentValue: "current",
        error: null,
        dirty: false,
    };

    async componentDidMount(): Promise<void> {
        if (!!this.props.settings) {
            const known = new KnownSettings(this.props.settings);
            known.sA_ReplacementHorizon = known.sA_ReplacementHorizon ?? 0;
            known.sU_ReplacementHorizon = known.sU_ReplacementHorizon ?? 0;
            this.setState({
                settings: known,
                teacherValue: this.getHorizonType(known.sA_ReplacementHorizon),
                studentValue: this.getHorizonType(known.sU_ReplacementHorizon),
            });
        }
    }

    componentDidUpdate(prevProps: Readonly<SettingsPortalsProps>): void {
        if (prevProps.settings !== this.props.settings) {
            const known = new KnownSettings(this.props.settings);
            known.sA_ReplacementHorizon = known.sA_ReplacementHorizon ?? 0;
            known.sU_ReplacementHorizon = known.sU_ReplacementHorizon ?? 0;
            this.setState({
                settings: known,
                teacherValue: this.getHorizonType(known.sA_ReplacementHorizon),
                studentValue: this.getHorizonType(known.sU_ReplacementHorizon),
            });
        }
    }

    getHorizonType(horizon: number) {
        switch (horizon) {
            case 0:
                return "current";
            case -1:
                return "all";
            default:
                return "custom";
        }
    }

    update(name: string, value: any) {
        this.props.onDirtyChange?.(true);

        return new Promise<void>(resolve => {
            const temp = new KnownSettings(this.state.settings.data);
            temp[name] = value;
            this.setState(
                {
                    settings: temp,
                    dirty: true,
                },
                () => resolve(),
            );
        });
    }

    async toggleHorizon(
        name: "teacherValue" | "studentValue",
        value: "current" | "all" | "custom",
    ) {
        switch (name) {
            case "teacherValue":
                await this.update(
                    "sA_ReplacementHorizon",
                    value === "current" ? 0 : value === "all" ? -1 : 7,
                );
                this.setState({
                    teacherValue: value,
                    dirty: true,
                });
                break;
            case "studentValue":
                await this.update(
                    "sU_ReplacementHorizon",
                    value === "current" ? 0 : value === "all" ? -1 : 7,
                );
                this.setState({
                    studentValue: value,
                    dirty: true,
                });
                break;
        }
    }

    reset() {
        this.props.onDirtyChange?.(false);

        const known = new KnownSettings(this.props.settings);
        this.setState({
            settings: known,
            teacherValue: this.getHorizonType(known.sA_ReplacementHorizon),
            studentValue: this.getHorizonType(known.sU_ReplacementHorizon),
            dirty: false,
        });
    }

    async save() {
        if (await this.context.verifyCredentials()) {
            try {
                this.props.setPageLoading(true);

                await updateKnownSettings(this.state.settings);

                this.props.onDirtyChange?.(false);

                this.setState({
                    dirty: false,
                });
            } finally {
                this.props.setPageLoading(false);
            }
        }
    }

    render() {
        return (
            <>
                <Panel>
                    <PanelHeader direction={RenderOrientations.RightLeft}>
                        <Button
                            icon="save"
                            secure={true}
                            theme={ButtonThemes.Theme}
                            disabled={!this.state.dirty || !!this.state.error}
                            onClick={this.save.bind(this)}
                            tooltip="Save changes"
                            tooltipPlace="bottom"
                        />
                        <Button
                            icon="history"
                            clearanceSide={Side.Right}
                            clearanceSize={Size.Small}
                            disabled={!this.state.dirty}
                            onClick={this.reset.bind(this)}
                            tooltip="Reset to last saved"
                            tooltipPlace="bottom"
                        />
                    </PanelHeader>

                    <Panel clearanceSide={Side.Top}>
                        <PanelHeader>
                            <h1>Teacher Portal Configuration</h1>
                        </PanelHeader>

                        <Panel>
                            <InputForm
                                display={RenderOrientations.Vertical}
                                fieldDisplay={RenderOrientations.Horizontal}>
                                <InputFormField
                                    type={InputFieldTypes.Radio}
                                    label="Show replacement classes up to (and including)"
                                    labelBold={true}
                                    value={this.state.teacherValue}
                                    onChange={(_, value) =>
                                        this.toggleHorizon(
                                            "teacherValue",
                                            value,
                                        )
                                    }
                                    options={[
                                        {
                                            text: "Current date",
                                            value: "current",
                                        },
                                        {
                                            text: "All dates",
                                            value: "all",
                                        },
                                        {
                                            text: "Custom number of days",
                                            value: "custom",
                                        },
                                    ]}
                                />
                                {this.state.teacherValue === "custom" && (
                                    <InputFormField
                                        fieldWidth={200}
                                        type={InputFieldTypes.Number}
                                        label="Custom Replacement Horizon (days)"
                                        labelBold={true}
                                        minNumber={1}
                                        maxNumber={30}
                                        value={
                                            this.state.settings
                                                ?.sA_ReplacementHorizon
                                        }
                                        onChange={(_, value) =>
                                            this.update(
                                                "sA_ReplacementHorizon",
                                                value,
                                            )
                                        }
                                    />
                                )}
                                <InputFormField
                                    type={InputFieldTypes.Check}
                                    label="Include Bulletin report"
                                    labelBold={true}
                                    value={
                                        !this.state.settings
                                            ?.sA_HideStaffBulletin
                                    }
                                    onChange={value =>
                                        this.update(
                                            "sA_HideStaffBulletin",
                                            value,
                                        )
                                    }
                                />
                            </InputForm>
                        </Panel>
                    </Panel>

                    <Panel clearanceSide={Side.Top}>
                        <PanelHeader>
                            <h1>Student Portal Configuration</h1>
                        </PanelHeader>

                        <Panel>
                            <InputForm
                                display={RenderOrientations.Vertical}
                                fieldDisplay={RenderOrientations.Horizontal}>
                                <InputFormField
                                    type={InputFieldTypes.Radio}
                                    label="Show replacement classes up to (and including)"
                                    labelBold={true}
                                    value={this.state.studentValue}
                                    onChange={(_, value) =>
                                        this.toggleHorizon(
                                            "studentValue",
                                            value,
                                        )
                                    }
                                    options={[
                                        {
                                            text: "Current date",
                                            value: "current",
                                        },
                                        {
                                            text: "All dates",
                                            value: "all",
                                        },
                                        {
                                            text: "Custom number of days",
                                            value: "custom",
                                        },
                                    ]}
                                />
                                {this.state.studentValue === "custom" && (
                                    <InputFormField
                                        fieldWidth={200}
                                        type={InputFieldTypes.Number}
                                        label="Custom Replacement Horizon (days)"
                                        labelBold={true}
                                        minNumber={1}
                                        maxNumber={30}
                                        value={
                                            this.state.settings
                                                ?.sU_ReplacementHorizon
                                        }
                                        onChange={(_, value) =>
                                            this.update(
                                                "sU_ReplacementHorizon",
                                                value,
                                            )
                                        }
                                    />
                                )}
                                <InputFormField
                                    type={InputFieldTypes.Check}
                                    label="Show student roll"
                                    labelBold={true}
                                    value={this.state.settings?.sU_ShowStudents}
                                    onChange={(_, value) =>
                                        this.update("sU_ShowStudents", value)
                                    }
                                />
                            </InputForm>
                        </Panel>
                    </Panel>
                </Panel>
            </>
        );
    }
}

const mapPortalsStateToProps = (state: IAppState) => {
    const { school, settings } = state.db;
    const { session } = state.session;
    return {
        user: session.user,
        school: school.school,
        settings: settings.settings,
    };
};

const mapPortalsDispatchToProps = dispatch => {
    return {
        setPageLoading: (value: boolean) => dispatch(setPageLoading(value)),
    };
};

const ConnectedSettingsPortals = connect(
    mapPortalsStateToProps,
    mapPortalsDispatchToProps,
)(SettingsPortals);
